import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  Bookmark,
  Camera,
  CalendarDays,
  Check,
  ChevronRight,
  CircleCheck,
  FileImage,
  Heart,
  ImagePlus,
  KeyRound,
  Leaf,
  LogOut,
  MapPin,
  MessageCircle,
  MoreHorizontal,
  PawPrint,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  Trash2,
  Trophy,
  UserRoundCheck,
  UserRound,
  X,
} from "lucide-react";
import { Geolocation } from "@capacitor/geolocation";
import { snapdom } from "@zumer/snapdom";
import * as THREE from "three";
import type { AreaScope, CatCard, Comment } from "./types";
import {
  createComment,
  createDemoCard,
  suggestedArea,
} from "./services/demoApi";
import { analyzeAnimal, requestCatCutout, uploadImage } from "./services/catCutout";
import { changePassword, deleteAccount as deleteServerAccount, loginWithPassword, registerAccount, revokeAllSessions, updatePublicProfile, type AuthSession } from "./services/auth";
import { createCard, deleteCard as deleteServerCard, fetchMyCards, fetchPublishedCards, resubmitCard } from "./services/cards";
import { boostCard, createPost, createReply, createServerComment, deleteComment as deleteServerComment, deletePost as deleteServerPost, fetchComments, fetchInteractions, fetchMyPosts, fetchPublishedPosts, fetchReplies, resubmitPost, toggleFavorite, toggleFollow, type SavedPost } from "./services/community";
import { fetchNotifications, markAllNotificationsRead, markNotificationRead, type AppNotification } from "./services/notifications";
import { claimChallengeReward, fetchChallengeState, type ChallengeState } from "./services/challenges";
import { ApiError } from "./services/http";

const initialCards: CatCard[] = [
  {
    id: "mianhua",
    species: "cat",
    code: "CAT-2026-02184",
    name: "棉花糖",
    emoji: "🐈",
    palette: "linear-gradient(145deg,#dca45a,#6e4935)",
    coat: "橘白",
    feature: "左耳有月牙形缺口",
    observedAt: "今天",
    area: {
      province: "浙江省",
      city: "杭州市",
      district: "西湖区",
      street: "文新街道",
    },
    observation: "AI 视觉观察：保持距离，但会用眼神回应靠近的人。",
    game: {
      rarity: "SSR",
      courage: 3.5,
      affinity: 4.5,
      curiosity: 3.5,
      stealth: 2.0,
      appetite: 4.0,
      speed: 2.0,
      agility: 3.0,
      intelligence: 4.5,
    },
    likes: 248,
    boostCount: 73,
  },
  {
    id: "hei",
    species: "cat",
    code: "CAT-2026-01896",
    name: "小煤球",
    emoji: "🐈",
    palette: "linear-gradient(145deg,#4d5761,#12161d)",
    coat: "纯黑",
    feature: "胸口有一撮白毛",
    observedAt: "昨天",
    area: {
      province: "浙江省",
      city: "杭州市",
      district: "西湖区",
      street: "翠苑街道",
    },
    observation: "AI 视觉观察：行动敏捷，似乎熟悉这片绿荫。",
    game: {
      rarity: "SR",
      courage: 3.0,
      affinity: 2.5,
      curiosity: 4.0,
      stealth: 5.0,
      appetite: 3.0,
      speed: 4.5,
      agility: 4.5,
      intelligence: 3.5,
    },
    likes: 126,
    boostCount: 84,
  },
  {
    id: "hua",
    species: "cat",
    code: "CAT-2026-01532",
    name: "花卷",
    emoji: "🐱",
    palette: "linear-gradient(145deg,#d7cfbe,#7e654f)",
    coat: "狸花",
    feature: "尾尖偏深",
    observedAt: "3 天前",
    area: {
      province: "浙江省",
      city: "杭州市",
      district: "西湖区",
      street: "灵隐街道",
    },
    observation: "AI 视觉观察：正处于放松观察状态。",
    game: {
      rarity: "R",
      courage: 2.5,
      affinity: 4.0,
      curiosity: 4.5,
      stealth: 3.0,
      appetite: 4.5,
      speed: 3.5,
      agility: 3.5,
      intelligence: 4.0,
    },
    likes: 87,
    boostCount: 31,
  },
  {
    id: "doubao",
    species: "dog",
    code: "DOG-2026-01076",
    name: "豆包",
    emoji: "🐕",
    palette: "linear-gradient(145deg,#d1a56a,#795438)",
    coat: "浅棕白",
    feature: "右耳微微下垂",
    observedAt: "今天",
    area: {
      province: "浙江省",
      city: "杭州市",
      district: "西湖区",
      street: "文新街道",
    },
    observation: "AI 视觉观察：保持适当距离，会抬头留意周围的脚步声。",
    game: {
      rarity: "SR",
      courage: 4.0,
      affinity: 3.5,
      curiosity: 4.0,
      stealth: 1.5,
      appetite: 4.0,
      speed: 3.5,
      agility: 3.0,
      intelligence: 3.5,
    },
    likes: 96,
    boostCount: 55,
  },
];

const starterComments: Comment[] = [
  {
    id: "c1",
    catId: "mianhua",
    author: "路过的摄影师",
    avatar: "路",
    text: "它今天还在那棵树旁边晒太阳，状态很好。",
    createdAt: "12 分钟前",
    status: "published",
  },
  {
    id: "c2",
    catId: "mianhua",
    author: "一粒麦子",
    avatar: "麦",
    text: "左耳的记号太有辨识度了。",
    createdAt: "1 小时前",
    status: "published",
  },
  {
    id: "c3",
    catId: "hei",
    author: "夜行人",
    avatar: "夜",
    text: "昨晚见到它从花坛里钻出来。",
    createdAt: "昨天",
    status: "published",
  },
];

type Tab = "home" | "challenges" | "community" | "following" | "atlas" | "profile";
type Screen = "main" | "create" | "detail" | "thread" | "post" | "myPosts";
type FeedScope = "all" | "city" | "nearby";
type SpeciesFilter = "all" | "dog" | "cat";
type CommunityPost = {
  id: string;
  section: "pets" | "adoption";
  title: string;
  text: string;
  image?: string;
  cardId: string;
  createdAt: string;
  author?: string;
  authorId?: string;
  isMine?: boolean;
  moderationStatus?: "pending" | "published" | "hidden";
  moderationReason?: string;
};
type PublicProfile = {
  nickname: string;
  bio: string;
  city: string;
  joinedAt: string;
  avatar?: string;
};
const toCommunityPost = (post: SavedPost): CommunityPost => ({
  id: post.id,
  section: post.section === "adoption" ? "adoption" : "pets",
  title: post.title,
  text: post.body,
  image: post.imageUrl || undefined,
  cardId: post.cardId || "",
  createdAt: post.createdAt || "刚刚",
  author: post.author,
  authorId: post.authorId,
  isMine: post.isMine,
  moderationStatus: post.status,
  moderationReason: post.moderationReason,
});
const rarityOrder = ["N", "R", "SR", "SSR", "UR"] as const;
const COMMUNITY_ENABLED = import.meta.env.VITE_COMMUNITY_ENABLED !== "false";

type AchievementGroup = "记录里程" | "动物伙伴" | "外观收藏" | "城市足迹" | "稀有卡牌";
type AchievementProgress = {
  id: string;
  group: AchievementGroup;
  icon: string;
  title: string;
  detail: string;
  value: number;
  goal: number;
};

function getChallengeMetrics(cards: CatCard[]) {
  return {
    coatCount: new Set(cards.map((card) => card.coat.trim()).filter(Boolean)).size,
    speciesCount: new Set(cards.map((card) => card.species)).size,
    catCount: cards.filter((card) => card.species === "cat").length,
    dogCount: cards.filter((card) => card.species === "dog").length,
    cityCount: new Set(cards.map((card) => card.area.city).filter(Boolean)).size,
    districtCount: new Set(cards.map((card) => `${card.area.city}-${card.area.district}`)).size,
    streetCount: new Set(cards.map((card) => `${card.area.city}-${card.area.district}-${card.area.street}`)).size,
    rareCount: cards.filter((card) => rarityOrder.indexOf(card.game.rarity) >= rarityOrder.indexOf("SR")).length,
    ssrCount: cards.filter((card) => rarityOrder.indexOf(card.game.rarity) >= rarityOrder.indexOf("SSR")).length,
    todayCount: cards.filter((card) => card.observedAt === "今天").length,
  };
}

function getChallengeAchievements(cards: CatCard[]): AchievementProgress[] {
  const metrics = getChallengeMetrics(cards);
  return [
    { id: "first", group: "记录里程", icon: "✦", title: "初次相遇", detail: "完成第一张动物卡", value: cards.length, goal: 1 },
    { id: "three", group: "记录里程", icon: "③", title: "三次相遇", detail: "累计收集 3 张动物卡", value: cards.length, goal: 3 },
    { id: "five", group: "记录里程", icon: "⑤", title: "城市观察者", detail: "累计收集 5 张动物卡", value: cards.length, goal: 5 },
    { id: "ten", group: "记录里程", icon: "⑩", title: "图鉴收藏家", detail: "累计收集 10 张动物卡", value: cards.length, goal: 10 },
    { id: "twenty", group: "记录里程", icon: "20", title: "城市守望者", detail: "累计收集 20 张动物卡", value: cards.length, goal: 20 },
    { id: "cats", group: "动物伙伴", icon: "猫", title: "猫咪朋友", detail: "记录 3 只城市猫咪", value: metrics.catCount, goal: 3 },
    { id: "dogs", group: "动物伙伴", icon: "犬", title: "狗狗朋友", detail: "记录 3 只城市狗狗", value: metrics.dogCount, goal: 3 },
    { id: "friends", group: "动物伙伴", icon: "双", title: "猫狗伙伴", detail: "猫猫和狗狗各记录一次", value: metrics.speciesCount, goal: 2 },
    { id: "coats", group: "外观收藏", icon: "彩", title: "外观收藏家", detail: "解锁 4 种外观标签", value: metrics.coatCount, goal: 4 },
    { id: "coats-eight", group: "外观收藏", icon: "虹", title: "百变图鉴", detail: "解锁 8 种外观标签", value: metrics.coatCount, goal: 8 },
    { id: "districts", group: "城市足迹", icon: "城", title: "城市漫游者", detail: "记录 3 个不同区县", value: metrics.districtCount, goal: 3 },
    { id: "streets", group: "城市足迹", icon: "巷", title: "街巷记录员", detail: "记录 5 个不同街道或乡镇", value: metrics.streetCount, goal: 5 },
    { id: "cities", group: "城市足迹", icon: "旅", title: "跨城旅人", detail: "在 2 个城市留下图鉴记录", value: metrics.cityCount, goal: 2 },
    { id: "rare", group: "稀有卡牌", icon: "SR", title: "闪光相遇", detail: "收集 1 张 SR 或更高卡牌", value: metrics.rareCount, goal: 1 },
    { id: "ssr", group: "稀有卡牌", icon: "金", title: "金色收藏", detail: "收集 1 张 SSR 或 UR 卡牌", value: metrics.ssrCount, goal: 1 },
  ];
}

function CatVisual({
  card,
  className = "",
}: {
  card: CatCard;
  className?: string;
}) {
  return card.image ? (
    <img
      className={`cat-image ${className}`}
      src={card.image}
      alt={`${card.name}的照片`}
    />
  ) : (
    <div
      className={`cat-visual ${className}`}
      style={{ background: card.palette }}
      aria-label={`${card.name}的示意图`}
    >
      <span>{card.emoji}</span>
    </div>
  );
}

function AreaLabel({
  area,
  detailed = false,
}: {
  area: AreaScope;
  detailed?: boolean;
}) {
  return (
    <span>
      {detailed
        ? `${area.city} · ${area.district} · ${area.street}`
        : `${area.city} · ${area.district}`}
    </span>
  );
}

export default function App() {
  const [tab, setTab] = useState<Tab>("home");
  const [screen, setScreen] = useState<Screen>("main");
  const [cards, setCards] = useState<CatCard[]>(initialCards);
  const [comments, setComments] = useState<Comment[]>(starterComments);
  const [followedAuthors, setFollowedAuthors] = useState<string[]>([]);
  const [savedCardIds, setSavedCardIds] = useState<string[]>([]);
  const [savedPostIds, setSavedPostIds] = useState<string[]>([]);
  const [threadReplies, setThreadReplies] = useState<Comment[]>([]);
  const [communityPosts, setCommunityPosts] = useState<CommunityPost[]>([]);
  const [boostedCardIds, setBoostedCardIds] = useState<string[]>([]);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [feedScope, setFeedScope] = useState<FeedScope>("all");
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");
  const currentCity = "杭州市";
  const [selectedId, setSelectedId] = useState("mianhua");
  const [recentlyViewedIds, setRecentlyViewedIds] = useState<string[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState("p1");
  const [postSection, setPostSection] = useState<"pets" | "adoption">("pets");
  const [selectedOwnPostId, setSelectedOwnPostId] = useState<string>();
  const [session, setSession] = useState<AuthSession | undefined>(() => {
    try {
      const saved = window.localStorage.getItem("city-animal-atlas-session");
      const parsed = saved ? JSON.parse(saved) as AuthSession : undefined;
      return parsed && parsed.expiresAt > Date.now() ? parsed : undefined;
    } catch { return undefined; }
  });
  const signedIn = Boolean(session);
  const [authOpen, setAuthOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [profileSetupOpen, setProfileSetupOpen] = useState(false);
  const [accountLevel, setAccountLevel] = useState(1);
  const [profile, setProfile] = useState<PublicProfile>(() => {
    const fallback = {
      nickname: "新朋友",
      bio: "记录与城市动物的相遇。",
      city: "杭州市",
      joinedAt: "2026.07",
    };
    try {
      const saved = window.localStorage.getItem("city-animal-atlas-profile");
      return saved ? { ...fallback, ...JSON.parse(saved) } : fallback;
    } catch {
      return fallback;
    }
  });
  const [toast, setToast] = useState("");
  const selected = cards.find((card) => card.id === selectedId) ?? cards[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen, tab]);

  useEffect(() => {
    window.localStorage.setItem("city-animal-atlas-profile", JSON.stringify(profile));
  }, [profile]);

  useEffect(() => {
    if (session) window.localStorage.setItem("city-animal-atlas-session", JSON.stringify(session));
    else window.localStorage.removeItem("city-animal-atlas-session");
  }, [session]);

  useEffect(() => {
    if (!session) {
      setAccountLevel(1);
      return;
    }
    void fetchInteractions(session.token).then((data) => {
      setSavedCardIds(data.favorites.filter((item) => item.targetType === "card").map((item) => item.targetId));
      setSavedPostIds(data.favorites.filter((item) => item.targetType === "post").map((item) => item.targetId));
      setBoostedCardIds(data.boosts.map((item) => item.cardId));
      setFollowerCount(data.followerCount);
      setFollowingCount(data.follows.length);
    }).catch(() => undefined);
    void fetchChallengeState(session.token)
      .then((state) => setAccountLevel(state.profile.level))
      .catch(() => setAccountLevel(1));
  }, [session]);

  useEffect(() => {
    if (!session) {
      setNotifications([]);
      setUnreadNotificationCount(0);
      setNotificationsOpen(false);
      return;
    }
    let cancelled = false;
    const refresh = () => void fetchNotifications(session.token).then((data) => {
      if (cancelled) return;
      setNotifications(data.notifications);
      setUnreadNotificationCount(data.unreadCount);
    }).catch(() => undefined);
    const onVisibilityChange = () => { if (document.visibilityState === "visible") refresh(); };
    refresh();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => { cancelled = true; document.removeEventListener("visibilitychange", onVisibilityChange); };
  }, [session]);

  useEffect(() => {
    void fetchPublishedCards()
      .then(({ cards: remoteCards }) => {
        if (!remoteCards.length) return;
        setCards((current) => [
          ...remoteCards,
          ...current.filter((card) => !remoteCards.some((remote) => remote.id === card.id)),
        ]);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!session) {
      setCards((current) => current
        .filter((card) => !(card.isMine && card.moderationStatus && card.moderationStatus !== "published"))
        .map((card) => card.isMine ? { ...card, isMine: false, moderationReason: undefined } : card));
      return;
    }
    let cancelled = false;
    void fetchMyCards(session.token)
      .then(({ cards: ownedCards }) => {
        if (cancelled) return;
        setCards((current) => [
          ...ownedCards,
          ...current.filter((card) => !ownedCards.some((owned) => owned.id === card.id)),
        ]);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [session]);

  useEffect(() => {
    if (!COMMUNITY_ENABLED) return;
    void fetchPublishedPosts().then(({ posts }) => {
      const remote = posts.map(toCommunityPost);
      if (!remote.length) return;
      setCommunityPosts((current) => [...remote, ...current.filter((item) => !remote.some((saved) => saved.id === item.id))]);
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!COMMUNITY_ENABLED) return;
    if (!session) {
      setCommunityPosts((current) => current
        .filter((post) => !(post.isMine && post.moderationStatus && post.moderationStatus !== "published"))
        .map((post) => post.isMine ? { ...post, isMine: false, moderationReason: undefined } : post));
      return;
    }
    let cancelled = false;
    void fetchMyPosts(session.token).then(({ posts }) => {
      if (cancelled) return;
      const ownedPosts = posts.map(toCommunityPost);
      setCommunityPosts((current) => [...ownedPosts, ...current.filter((post) => !ownedPosts.some((owned) => owned.id === post.id))]);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [session]);

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(""), 2400);
  };
  const completeSignIn = (nextSession: AuthSession) => {
    setSession(nextSession);
    setProfile((current) => ({ ...current, nickname: nextSession.user.nickname, avatar: nextSession.user.avatarUrl || current.avatar }));
    setAuthOpen(false);
    setSecurityOpen(false);
    setNotificationsOpen(false);
    setProfileSetupOpen(true);
    showToast("登录成功，请完善公开资料");
  };
  const profileSetupSheet = profileSetupOpen ? (
    <ProfileSetupSheet
      profile={profile}
      onSave={async (nextProfile, avatarFile) => {
        if (!session) return;
        try {
          const avatarUrl = avatarFile ? await uploadImage(avatarFile, session.token) : profile.avatar;
          const updated = await updatePublicProfile(session.token, { nickname: nextProfile.nickname, avatarUrl });
          setSession({ ...session, user: updated.user });
          setProfile({ ...nextProfile, nickname: updated.user.nickname, avatar: updated.user.avatarUrl || undefined });
          setProfileSetupOpen(false);
          showToast("公开资料已保存");
        } catch {
          showToast("资料保存失败，请检查网络后重试");
        }
      }}
    />
  ) : null;
  const requireLogin = (action: () => void) =>
    signedIn ? action() : setAuthOpen(true);
  const openDetail = (id: string) => {
    setSelectedId(id);
    setRecentlyViewedIds((ids) =>
      [id, ...ids.filter((item) => item !== id)].slice(0, 8),
    );
    setScreen("detail");
  };
  const openThread = (id: string) => {
    setSelectedThreadId(id);
    setScreen("thread");
  };
  const openPostComposer = (section: "pets" | "adoption") => {
    setPostSection(section);
    setScreen("post");
  };
  const openNotificationTarget = async (notification: AppNotification) => {
    if (!session) { setAuthOpen(true); return; }
    if (!notification.readAt) {
      setNotifications((items) => items.map((item) => item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item));
      setUnreadNotificationCount((count) => Math.max(0, count - 1));
      void markNotificationRead(session.token, notification.id).catch(() => undefined);
    }
    setNotificationsOpen(false);
    if (notification.targetType === "card" && notification.targetId) {
      try {
        const { cards: ownedCards } = await fetchMyCards(session.token);
        setCards((current) => [...ownedCards, ...current.filter((card) => !ownedCards.some((owned) => owned.id === card.id))]);
        if (ownedCards.some((card) => card.id === notification.targetId) || cards.some((card) => card.id === notification.targetId)) {
          openDetail(notification.targetId);
          return;
        }
      } catch { /* keep the notification readable even if refresh fails */ }
      showToast("对应的动物卡暂时无法打开");
      return;
    }
    if (notification.targetType === "post" && notification.targetId) {
      if (!COMMUNITY_ENABLED) {
        showToast("社区功能暂未开放，这条历史通知仅作保留");
        return;
      }
      if (notification.type === "moderation_hidden" && notification.title.startsWith("帖子")) {
        try {
          const { posts } = await fetchMyPosts(session.token);
          const ownedPosts = posts.map(toCommunityPost);
          setCommunityPosts((current) => [...ownedPosts, ...current.filter((post) => !ownedPosts.some((owned) => owned.id === post.id))]);
          setSelectedOwnPostId(notification.targetId);
          setScreen("myPosts");
          return;
        } catch { /* handled below */ }
        showToast("对应的帖子暂时无法打开");
        return;
      }
      try {
        const { posts } = await fetchPublishedPosts();
        const remote = posts.map(toCommunityPost);
        setCommunityPosts((current) => [...remote, ...current.filter((item) => !remote.some((saved) => saved.id === item.id))]);
        if (remote.some((post) => post.id === notification.targetId)) { openThread(notification.targetId); return; }
      } catch { /* handled by toast below */ }
      showToast("对应的帖子暂时无法打开");
    }
  };
  const addCommunityPost = (post: CommunityPost) => {
    setCommunityPosts((posts) => [{ ...post, isMine: true, author: profile.nickname, authorId: session?.user.id }, ...posts]);
    setTab(post.moderationStatus === "published" ? "community" : "profile");
    setScreen("main");
    showToast(post.moderationStatus === "pending" ? "帖子已提交审核，可在我的发布查看进度" : "帖子已发布");
  };
  const addThreadReply = (reply: Comment) => {
    setThreadReplies((items) => [...items, reply]);
    showToast(
      reply.status === "pending"
        ? "图文回复已提交审核，通过后会公开展示"
        : "回复已发布",
    );
  };
  const toggleSavedCard = (id: string) => requireLogin(() => {
    if (!session) return;
    void toggleFavorite(session.token, "card", id).then(({ saved }) => {
      setSavedCardIds((ids) => saved ? [...new Set([...ids, id])] : ids.filter((item) => item !== id));
      showToast(saved ? "已收藏动物卡" : "已取消收藏");
    }).catch(() => showToast("收藏失败，请稍后重试"));
  });
  const toggleSavedPost = (id: string) => requireLogin(() => {
    if (!session) return;
    void toggleFavorite(session.token, "post", id).then(({ saved }) => setSavedPostIds((ids) => saved ? [...new Set([...ids, id])] : ids.filter((item) => item !== id))).catch(() => showToast("收藏失败，请稍后重试"));
  });
  const addCard = (card: CatCard) => {
    setCards((existing) => [{ ...card, isMine: true, authorId: session?.user.id, author: card.author || profile.nickname }, ...existing]);
    setSelectedId(card.id);
    setScreen("detail");
    setTab("atlas");
    showToast(card.moderationStatus === "pending" ? "已提交审核，可在我的图鉴查看进度" : "已保存到我的图鉴");
  };
  const updateOwnedCard = (card: CatCard) => {
    setCards((items) => items.map((item) => item.id === card.id ? { ...card, isMine: true } : item));
  };
  const addComment = (comment: Comment) => {
    setComments((items) => [...items, comment]);
    showToast(
      comment.status === "pending"
        ? "评论已提交审核，审核通过后公开"
        : "评论已发布",
    );
  };
  const addBoost = (cardId: string) => {
    if (!session) return;
    void boostCard(session.token, cardId).then(({ card }) => {
      const previous = cards.find((item) => item.id === cardId);
      setCards((items) => items.map((item) => item.id === cardId ? card : item));
      setBoostedCardIds((ids) => [...new Set([...ids, cardId])]);
      showToast(previous && previous.game.rarity !== card.game.rarity
        ? `助力成功，${card.name} 已升为 ${card.game.rarity}！`
        : `助力成功，已有 ${card.boostCount} 人助力`);
    }).catch((caught: unknown) => {
      if (caught instanceof ApiError && caught.message === "already-boosted") {
        setBoostedCardIds((ids) => [...new Set([...ids, cardId])]);
        showToast("你已经为这张动物卡助力过了");
      } else if (caught instanceof ApiError && caught.message === "card-not-found") {
        showToast("卡牌数据尚未同步，请刷新后重试");
      } else {
        showToast("助力失败，请稍后重试");
      }
    });
  };

  const deleteOwnedCard = async (cardId: string) => {
    if (!session) { setAuthOpen(true); return; }
    if (!window.confirm("删除后，动物卡、相关评论和图片将无法恢复。确定删除吗？")) return;
    try {
      const result = await deleteServerCard(cardId, session.token);
      setCards((items) => items.filter((item) => item.id !== cardId));
      setSavedCardIds((ids) => ids.filter((id) => id !== cardId));
      setComments((items) => items.filter((item) => item.catId !== cardId));
      setCommunityPosts((items) => items.map((post) => post.cardId === cardId ? { ...post, cardId: "" } : post));
      setScreen("main");
      setTab("atlas");
      showToast(result.storageCleanupPending ? "动物卡已删除，部分图片待后台清理" : "动物卡和相关图片已删除");
    } catch {
      showToast("删除失败，请稍后重试");
    }
  };

  const deleteOwnedPost = async (postId: string, destination: Tab = "community") => {
    if (!session) { setAuthOpen(true); return; }
    if (!window.confirm("删除后，帖子、回复和图片将无法恢复。确定删除吗？")) return;
    try {
      const result = await deleteServerPost(session.token, postId);
      setCommunityPosts((items) => items.filter((item) => item.id !== postId));
      setThreadReplies((items) => items.filter((item) => item.catId !== postId));
      setSavedPostIds((ids) => ids.filter((id) => id !== postId));
      setScreen("main");
      setTab(destination);
      showToast(result.storageCleanupPending ? "帖子已删除，部分图片待后台清理" : "帖子和相关图片已删除");
    } catch {
      showToast("删除失败，请稍后重试");
    }
  };

  const deleteOwnedComment = async (commentId: string) => {
    if (!session) { setAuthOpen(true); return false; }
    if (!window.confirm("确定删除这条内容吗？删除后无法恢复。")) return false;
    try {
      const result = await deleteServerComment(session.token, commentId);
      setComments((items) => items.filter((item) => item.id !== commentId));
      setThreadReplies((items) => items.filter((item) => item.id !== commentId));
      showToast(result.storageCleanupPending ? "内容已删除，部分图片待后台清理" : "内容已删除");
      return true;
    } catch {
      showToast("删除失败，请稍后重试");
      return false;
    }
  };

  const deleteOwnAccount = async () => {
    if (!session) { setAuthOpen(true); return; }
    if (!window.confirm("注销账号会永久删除你的动物卡、帖子、评论、收藏及图片，且无法恢复。确定继续吗？")) return;
    try {
      const userId = session.user.id;
      const result = await deleteServerAccount(session.token);
      const ownedCardIds = new Set(cards.filter((card) => card.authorId === userId || card.isMine).map((card) => card.id));
      const ownedPostIds = new Set(communityPosts.filter((post) => post.authorId === userId).map((post) => post.id));
      setCards((items) => items.filter((item) => !ownedCardIds.has(item.id)));
      setCommunityPosts((items) => items.filter((item) => !ownedPostIds.has(item.id)));
      setComments((items) => items.filter((item) => !item.isMine));
      setThreadReplies((items) => items.filter((item) => !item.isMine));
      setSavedCardIds([]);
      setSavedPostIds([]);
      setBoostedCardIds([]);
      setFollowedAuthors([]);
      setFollowerCount(0);
      setFollowingCount(0);
      setSession(undefined);
      window.localStorage.removeItem("city-animal-atlas-profile");
      setScreen("main");
      setTab("home");
      showToast(result.storageCleanupPending ? "账号已注销，部分图片待后台清理" : "账号和相关数据已删除");
    } catch {
      showToast("账号注销失败，请稍后重试");
    }
  };

  if (screen === "create")
    return (
      <>
        <CreateScreen
          onBack={() => setScreen("main")}
          onCreated={addCard}
          requireLogin={requireLogin}
          showToast={showToast}
          sessionToken={session?.token}
        />
        {authOpen && (
          <AuthSheet
            onClose={() => setAuthOpen(false)}
            onSuccess={completeSignIn}
          />
        )}
        {toast && (
          <div className="toast">
            <Check size={17} />
            {toast}
          </div>
        )}
        {profileSetupSheet}
      </>
    );
  if (screen === "post")
    return (
      <>
        <PostComposer
          section={postSection}
          cards={cards.filter((card) => !card.moderationStatus || card.moderationStatus === "published")}
          recentCardIds={recentlyViewedIds}
          onBack={() => setScreen("main")}
          onPublish={addCommunityPost}
          requireLogin={requireLogin}
          showToast={showToast}
          sessionToken={session?.token}
        />
        {authOpen && (
          <AuthSheet
            onClose={() => setAuthOpen(false)}
            onSuccess={completeSignIn}
          />
        )}
        {toast && (
          <div className="toast">
            <Check size={17} />
            {toast}
          </div>
        )}
        {profileSetupSheet}
      </>
    );
  if (screen === "myPosts")
    return (
      <>
        <MyPostsScreen
          posts={communityPosts.filter((post) => post.isMine)}
          cards={cards.filter((card) => !card.moderationStatus || card.moderationStatus === "published")}
          selectedPostId={selectedOwnPostId}
          sessionToken={session?.token}
          onBack={() => { setSelectedOwnPostId(undefined); setScreen("main"); setTab("profile"); }}
          onOpenPost={openThread}
          onUpdated={(post) => setCommunityPosts((items) => items.map((item) => item.id === post.id ? post : item))}
          onDelete={(postId) => void deleteOwnedPost(postId, "profile")}
          showToast={showToast}
        />
        {toast && <div className="toast"><Check size={17} />{toast}</div>}
      </>
    );
  if (screen === "detail")
    return (
      <>
        <DetailScreen
          card={selected}
          comments={comments.filter((item) => item.catId === selected.id)}
          onBack={() => setScreen("main")}
          requireLogin={requireLogin}
          onComment={addComment}
          hasBoosted={boostedCardIds.includes(selected.id)}
          onBoost={addBoost}
          isSaved={savedCardIds.includes(selected.id)}
          onToggleSave={() => toggleSavedCard(selected.id)}
          showToast={showToast}
          sessionToken={session?.token}
          sessionUserId={session?.user.id}
          canDelete={Boolean(session && selected.authorId === session.user.id)}
          onDelete={() => void deleteOwnedCard(selected.id)}
          onDeleteComment={deleteOwnedComment}
          onCardUpdated={updateOwnedCard}
        />
        {authOpen && (
          <AuthSheet
            onClose={() => setAuthOpen(false)}
            onSuccess={completeSignIn}
          />
        )}
        {toast && (
          <div className="toast">
            <Check size={17} />
            {toast}
          </div>
        )}
        {profileSetupSheet}
      </>
    );
  if (screen === "thread")
    return (
      <>
        <ThreadScreen
          threadId={selectedThreadId}
          cards={cards}
          userPosts={communityPosts}
          replies={threadReplies.filter(
            (reply) => reply.catId === selectedThreadId,
          )}
          onBack={() => setScreen("main")}
          onOpenAnimal={openDetail}
          onReply={addThreadReply}
          requireLogin={requireLogin}
          showToast={showToast}
          sessionToken={session?.token}
          currentUserId={session?.user.id}
          onDeletePost={(postId) => void deleteOwnedPost(postId)}
          onDeleteReply={deleteOwnedComment}
        />
        {authOpen && (
          <AuthSheet
            onClose={() => setAuthOpen(false)}
            onSuccess={completeSignIn}
          />
        )}
        {toast && (
          <div className="toast">
            <Check size={17} />
            {toast}
          </div>
        )}
        {profileSetupSheet}
      </>
    );

  const visibleCards =
    tab === "atlas"
      ? cards.filter((card) => card.isMine || savedCardIds.includes(card.id))
      : tab === "home"
        ? cards.filter(
            (card) =>
              (!card.moderationStatus || card.moderationStatus === "published") &&
              (speciesFilter === "all" || card.species === speciesFilter) &&
              (feedScope === "all" ||
                (card.area.city === currentCity &&
                  (feedScope === "city" || card.area.district === "西湖区"))),
          )
        : cards;
  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <div className="brand-mark">
            <PawPrint size={20} />
          </div>
          <div>
            <strong>城市动物图鉴</strong>
            <small>记录每一次温柔相遇</small>
          </div>
        </div>
        <button
          className="icon-btn notification-button"
          aria-label={unreadNotificationCount ? `${unreadNotificationCount} 条未读通知` : "通知"}
          onClick={() => requireLogin(() => setNotificationsOpen(true))}
        >
          <Bell size={20} />
          {unreadNotificationCount > 0 && <span>{unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}</span>}
        </button>
      </header>
      <main className="content">
        {tab === "home" && (
          <HomeHeader
            onCreate={() => requireLogin(() => setScreen("create"))}
            onSearch={() => showToast("搜索将在城市内容积累后开放")}
            scope={feedScope}
            onScopeChange={setFeedScope}
            species={speciesFilter}
            onSpeciesChange={setSpeciesFilter}
          />
        )}
        {tab === "atlas" && (
          <AtlasHeader
            count={visibleCards.length}
            onCreate={() => requireLogin(() => setScreen("create"))}
          />
        )}
        {tab === "challenges" && (
          <ChallengePage
            cards={cards.filter((card) => card.isMine && (!card.moderationStatus || card.moderationStatus === "published"))}
            signedIn={signedIn}
            sessionToken={session?.token}
            showToast={showToast}
            onCreate={() => requireLogin(() => setScreen("create"))}
            onOpenCard={openDetail}
            onSignIn={() => setAuthOpen(true)}
          />
        )}
        {COMMUNITY_ENABLED && tab === "community" && (
          <CommunityHub
            cards={cards.filter((card) => !card.moderationStatus || card.moderationStatus === "published")}
            onOpen={openDetail}
            onOpenThread={openThread}
            userPosts={communityPosts.filter((post) => !post.moderationStatus || post.moderationStatus === "published")}
            onCompose={openPostComposer}
            savedPostIds={savedPostIds}
            onToggleSavePost={toggleSavedPost}
            followedAuthors={followedAuthors}
            onToggleFollow={(author, authorId) => requireLogin(() => {
              if (!session || !authorId) { showToast("示例作者暂未开通关注；发布后的真实用户可关注"); return; }
              void toggleFollow(session.token, authorId).then(({ following }) => {
                setFollowedAuthors((authors) => following ? [...new Set([...authors, author])] : authors.filter((item) => item !== author));
                setFollowingCount((count) => Math.max(0, count + (following ? 1 : -1)));
              }).catch(() => showToast("关注失败，请稍后重试"));
            })}
          />
        )}
        {tab === "following" && <FollowingPage authors={followedAuthors} />}
        {tab === "profile" && (
          <Profile
            signedIn={signedIn}
            level={accountLevel}
            onSignIn={() => setAuthOpen(true)}
            profile={profile}
            onEditProfile={() => setProfileSetupOpen(true)}
            cards={cards}
            onOpenCard={openDetail}
            onOpenAtlas={() => setTab("atlas")}
            onOpenChallenges={() => setTab("challenges")}
            onOpenSecurity={() => requireLogin(() => setSecurityOpen(true))}
            onOpenSafety={() => showToast("AI 内容均为趣味观察，不构成医疗或救助建议")}
            onDeleteAccount={() => void deleteOwnAccount()}
          />
        )}
        {tab !== "profile" && tab !== "challenges" && tab !== "community" && tab !== "following" && (
          <section className="card-grid" aria-label="猫咪卡片列表">
            {visibleCards.length === 0 ? (
              <EmptyAtlas
                onCreate={() => requireLogin(() => setScreen("create"))}
              />
            ) : (
              visibleCards.map((card) => (
                <CardTile
                  key={card.id}
                  card={card}
                  onOpen={() => openDetail(card.id)}
                  isSaved={savedCardIds.includes(card.id)}
                  onToggleSave={() => toggleSavedCard(card.id)}
                />
              ))
            )}
          </section>
        )}
      </main>
      <nav className="tabbar" aria-label="主导航">
        <NavItem
          active={tab === "home"}
          icon={<Leaf size={21} />}
          label="发现"
          onClick={() => setTab("home")}
        />
        <NavItem
          active={tab === "challenges"}
          icon={<Trophy size={21} />}
          label="挑战"
          onClick={() => setTab("challenges")}
        />
        <button
          className="capture-button"
          aria-label="记录一只动物"
          onClick={() => requireLogin(() => setScreen("create"))}
        >
          <Camera size={26} />
          <span>记录</span>
        </button>
        <NavItem
          active={tab === "atlas"}
          icon={<FileImage size={21} />}
          label="我的图鉴"
          onClick={() => setTab("atlas")}
        />
        <NavItem
          active={tab === "profile"}
          icon={<UserRound size={21} />}
          label="我的"
          onClick={() => setTab("profile")}
        />
      </nav>
      {authOpen && (
        <AuthSheet
          onClose={() => setAuthOpen(false)}
          onSuccess={completeSignIn}
        />
      )}
      {securityOpen && session && (
        <AccountSecuritySheet
          token={session.token}
          onClose={() => setSecurityOpen(false)}
          onPasswordChanged={() => showToast("密码已修改，其他设备已退出登录")}
          onAllSessionsRevoked={() => {
            setSecurityOpen(false);
            setSession(undefined);
            setSavedCardIds([]);
            setSavedPostIds([]);
            setBoostedCardIds([]);
            setFollowedAuthors([]);
            setFollowerCount(0);
            setFollowingCount(0);
            setTab("profile");
            showToast("所有设备均已退出，请重新登录");
          }}
        />
      )}
      {notificationsOpen && session && (
        <NotificationSheet
          notifications={notifications}
          unreadCount={unreadNotificationCount}
          onClose={() => setNotificationsOpen(false)}
          onOpen={(notification) => void openNotificationTarget(notification)}
          onReadAll={() => {
            setNotifications((items) => items.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
            setUnreadNotificationCount(0);
            void markAllNotificationsRead(session.token).catch(() => {
              showToast("标记已读失败，请稍后重试");
              void fetchNotifications(session.token).then((data) => { setNotifications(data.notifications); setUnreadNotificationCount(data.unreadCount); }).catch(() => undefined);
            });
          }}
        />
      )}
      {toast && (
        <div className="toast">
          <Check size={17} />
          {toast}
        </div>
      )}
      {profileSetupSheet}
    </div>
  );
}

function HomeHeader({
  onCreate,
  onSearch,
  scope,
  onScopeChange,
  species,
  onSpeciesChange,
}: {
  onCreate: () => void;
  onSearch: () => void;
  scope: FeedScope;
  onScopeChange: (scope: FeedScope) => void;
  species: SpeciesFilter;
  onSpeciesChange: (species: SpeciesFilter) => void;
}) {
  return (
    <>
      <section className="hero">
        <div>
          <p className="eyebrow">CITY ANIMAL ATLAS</p>
          <h1>
            发现身边的
            <br />
            城市动物
          </h1>
          <p>记录 · 观察 · 保护</p>
          <button className="primary-btn" onClick={onCreate}>
            <Camera size={19} />
            记录一只动物
          </button>
        </div>
      </section>
      <div className="section-heading">
        <div>
          <p className="eyebrow">CITY ENCOUNTERS</p>
          <h2>已发现的城市动物</h2>
        </div>
        <button className="text-btn" onClick={onSearch}>
          <Search size={17} />
          寻找动物
        </button>
      </div>
      <div className="city-feed-switcher" aria-label="猫咪卡片范围">
        <div className="animal-tabs" role="tablist" aria-label="动物种类">
          <button
            className={species === "all" ? "active" : ""}
            onClick={() => onSpeciesChange("all")}
            role="tab"
            aria-selected={species === "all"}
          >
            全部
          </button>
          <button
            className={species === "dog" ? "active" : ""}
            onClick={() => onSpeciesChange("dog")}
            role="tab"
            aria-selected={species === "dog"}
          >
            狗狗
          </button>
          <button
            className={species === "cat" ? "active" : ""}
            onClick={() => onSpeciesChange("cat")}
            role="tab"
            aria-selected={species === "cat"}
          >
            猫猫
          </button>
        </div>
        <div className="feed-tabs" role="tablist" aria-label="内容范围">
          <button
            className={scope === "all" ? "active" : ""}
            onClick={() => onScopeChange("all")}
            role="tab"
            aria-selected={scope === "all"}
          >
            所有
          </button>
          <button
            className={scope === "city" ? "active" : ""}
            onClick={() => onScopeChange("city")}
            role="tab"
            aria-selected={scope === "city"}
          >
            当前城市
          </button>
          <button
            className={scope === "nearby" ? "active" : ""}
            onClick={() => onScopeChange("nearby")}
            role="tab"
            aria-selected={scope === "nearby"}
          >
            附近
          </button>
        </div>
      </div>
    </>
  );
}

function AtlasHeader({
  count,
  onCreate,
}: {
  count: number;
  onCreate: () => void;
}) {
  return (
    <section className="atlas-header">
      <p className="eyebrow">MY ATLAS</p>
      <h1>我的相遇</h1>
      <p>
        已收藏 <strong>{count}</strong> 只城市小生命
      </p>
      <button className="outline-btn" onClick={onCreate}>
        <Plus size={17} />
        添加新记录
      </button>
    </section>
  );
}

function ChallengePage({
  cards,
  signedIn,
  sessionToken,
  showToast,
  onCreate,
  onOpenCard,
  onSignIn,
}: {
  cards: CatCard[];
  signedIn: boolean;
  sessionToken?: string;
  showToast: (text: string) => void;
  onCreate: () => void;
  onOpenCard: (id: string) => void;
  onSignIn: () => void;
}) {
  const [challengeState, setChallengeState] = useState<ChallengeState>();
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [challengeError, setChallengeError] = useState(false);
  const [claimingTaskId, setClaimingTaskId] = useState<string>();
  const [clock, setClock] = useState(Date.now());
  const refreshChallenge = () => {
    if (!sessionToken) return;
    setChallengeLoading(true);
    setChallengeError(false);
    void fetchChallengeState(sessionToken)
      .then(setChallengeState)
      .catch(() => setChallengeError(true))
      .finally(() => setChallengeLoading(false));
  };
  useEffect(() => { refreshChallenge(); }, [sessionToken, cards.length]);
  useEffect(() => {
    if (!challengeState?.week.endsAt) return;
    const timer = window.setInterval(() => setClock(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, [challengeState?.week.endsAt]);
  const localMetrics = getChallengeMetrics(cards);
  const localAchievements = getChallengeAchievements(cards);
  const metrics = challengeState?.metrics || { ...localMetrics, cards: cards.length };
  const achievements = challengeState?.achievements || localAchievements.map((item) => ({ ...item, unlocked: item.value >= item.goal, unlockedAt: null }));
  const achievementGroups: AchievementGroup[] = ["记录里程", "动物伙伴", "外观收藏", "城市足迹", "稀有卡牌"];
  const unlockedCount = achievements.filter((item) => item.unlocked).length;
  const experience = challengeState?.profile.experience ?? cards.length * 25 + unlockedCount * 40;
  const level = challengeState?.profile.level ?? Math.floor(experience / 200) + 1;
  const levelProgress = challengeState?.profile.levelProgress ?? experience % 200;
  const tasks = challengeState?.tasks || [
    { id: "record_this_week", title: "记录本周相遇", detail: "本周完成 1 张公开动物卡", value: 0, goal: 1, rewardExperience: 60, completed: false, claimed: false, claimedAt: null },
    { id: "weekly_coats", title: "发现不同外观", detail: "本周记录 2 种不同外观标签", value: 0, goal: 2, rewardExperience: 80, completed: false, claimed: false, claimedAt: null },
    { id: "both_species", title: "认识不同伙伴", detail: "本周猫猫与狗狗各记录 1 次", value: 0, goal: 2, rewardExperience: 100, completed: false, claimed: false, claimedAt: null },
  ];
  const remainingMs = Math.max(0, new Date(challengeState?.week.endsAt || 0).getTime() - clock);
  const remainingDays = Math.floor(remainingMs / 86_400_000);
  const remainingHours = Math.floor((remainingMs % 86_400_000) / 3_600_000);
  const weekLabel = challengeState ? `还剩 ${remainingDays} 天 ${remainingHours} 小时` : "每周一刷新";
  const claimReward = (taskId: string) => {
    if (!sessionToken || claimingTaskId) return;
    const previousLevel = level;
    setClaimingTaskId(taskId);
    void claimChallengeReward(sessionToken, taskId).then((state) => {
      setChallengeState(state);
      const reward = state.claimedRewardExperience || 0;
      showToast(state.profile.level > previousLevel ? `领取成功，升级至 Lv.${state.profile.level}` : `已领取 +${reward} 经验`);
    }).catch(() => showToast("奖励领取失败，请刷新后重试"))
      .finally(() => setClaimingTaskId(undefined));
  };
  const themes = [
    { title: "百变外观", detail: "收集不同花色与毛色", value: metrics.coatCount, goal: 8, tone: "amber" },
    { title: "城市足迹", detail: "只统计城市与区县，不展示具体位置", value: metrics.districtCount, goal: 5, tone: "green" },
    { title: "街巷相遇", detail: "记录不同街道或乡镇", value: metrics.streetCount, goal: 6, tone: "clay" },
    { title: "珍稀卡册", detail: "收集 SR 或更高等级卡牌", value: metrics.rareCount, goal: 3, tone: "ink" },
  ];
  const latestCard = cards[0];
  return (
    <section className="challenge-page">
      <div className="challenge-hero">
        <div className="challenge-hero-copy">
          <p className="eyebrow">ATLAS CHALLENGES</p>
          <h1>让每次相遇<br />点亮一格图鉴</h1>
          <p>只记录个人收集进度，不设公开排名。</p>
          <button type="button" onClick={signedIn ? onCreate : onSignIn}>
            <Camera size={18} /> {signedIn ? "记录新相遇" : "登录后开始挑战"}
          </button>
        </div>
        <div className="challenge-level" aria-label={`当前等级 ${level}`}>
          <span>LV.</span><strong>{level}</strong><small>{levelProgress}/200</small>
        </div>
      </div>

      <section className="challenge-summary" aria-label="图鉴挑战进度">
        <div><strong>{cards.length}</strong><span>动物卡</span></div>
        <div><strong>{metrics.coatCount}</strong><span>外观标签</span></div>
        <div><strong>{unlockedCount}</strong><span>成就徽章</span></div>
      </section>

      {!signedIn && (
        <div className="challenge-signin-note">
          <ShieldCheck size={19} />
          <span><strong>进度仅保存在你的账户中</strong><small>登录后根据“我的图鉴”自动计算，不公开个人挑战数据。</small></span>
        </div>
      )}

      <div className="challenge-section-heading">
        <div><p className="eyebrow">WEEKLY QUESTS</p><h2>本期挑战</h2></div>
        <span>{weekLabel} · {tasks.filter((task) => task.completed).length}/{tasks.length}</span>
      </div>
      {challengeError && <button className="challenge-retry" type="button" onClick={refreshChallenge}>进度加载失败，点击重试</button>}
      <div className="challenge-task-list">
        {tasks.map((task) => {
          const done = task.completed;
          const progress = Math.min(100, (task.value / task.goal) * 100);
          return (
            <article className={done ? "is-complete" : ""} key={task.title}>
              <span className="challenge-task-icon">{done ? <CircleCheck size={21} /> : <Target size={20} />}</span>
              <div><strong>{task.title}</strong><small>{task.detail}</small><i><b style={{ width: `${progress}%` }} /></i></div>
              {task.claimed ? <em className="is-claimed">已领取</em> : done && signedIn ? (
                <button className="challenge-claim" type="button" disabled={claimingTaskId === task.id || challengeLoading} onClick={() => claimReward(task.id)}>
                  {claimingTaskId === task.id ? "领取中" : `领 +${task.rewardExperience}`}
                </button>
              ) : <em>{Math.min(task.value, task.goal)}/{task.goal}</em>}
            </article>
          );
        })}
      </div>

      <div className="challenge-section-heading">
        <div><p className="eyebrow">THEMED ATLAS</p><h2>主题图鉴</h2></div>
        <CalendarDays size={19} />
      </div>
      <div className="challenge-theme-grid">
        {themes.map((theme) => {
          const progress = Math.min(100, (theme.value / theme.goal) * 100);
          return (
            <article className={`challenge-theme-card tone-${theme.tone}`} key={theme.title}>
              <span>{Math.round(progress)}%</span>
              <div><strong>{theme.title}</strong><small>{theme.detail}</small></div>
              <i><b style={{ width: `${progress}%` }} /></i>
              <em>{Math.min(theme.value, theme.goal)} / {theme.goal}</em>
            </article>
          );
        })}
      </div>

      <div className="challenge-section-heading">
        <div><p className="eyebrow">ACHIEVEMENTS</p><h2>成就徽章</h2></div>
        <span>{unlockedCount}/{achievements.length} 解锁</span>
      </div>
      <div className="achievement-groups">
        {achievementGroups.map((group) => {
          const groupAchievements = achievements.filter((achievement) => achievement.group === group);
          const groupUnlocked = groupAchievements.filter((achievement) => achievement.value >= achievement.goal).length;
          return (
            <section className="achievement-group" key={group}>
              <div className="achievement-group-heading"><strong>{group}</strong><span>{groupUnlocked}/{groupAchievements.length}</span></div>
              <div className="achievement-grid">
                {groupAchievements.map((achievement) => {
                  const unlocked = achievement.unlocked;
                  return (
                    <article className={unlocked ? "is-unlocked" : ""} key={achievement.id}>
                      <span>{achievement.icon}</span>
                      <strong>{achievement.title}</strong>
                      <small>{unlocked ? "已解锁" : achievement.detail}</small>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {latestCard && (
        <button className="challenge-latest" type="button" onClick={() => onOpenCard(latestCard.id)}>
          <CatVisual card={latestCard} />
          <span><small>最近点亮</small><strong>{latestCard.name}</strong><em>{latestCard.coat} · {latestCard.game.rarity}</em></span>
          <ChevronRight size={18} />
        </button>
      )}
      <p className="challenge-safety-note"><Leaf size={14} /> 请保持距离观察，不要为了完成挑战追逐、惊扰或不当投喂动物。</p>
    </section>
  );
}

function CommunityHub({
  cards,
  onOpen,
  onOpenThread,
  userPosts,
  onCompose,
  savedPostIds,
  onToggleSavePost,
  followedAuthors,
  onToggleFollow,
}: {
  cards: CatCard[];
  onOpen: (id: string) => void;
  onOpenThread: (id: string) => void;
  userPosts: CommunityPost[];
  onCompose: (section: "pets" | "adoption") => void;
  savedPostIds: string[];
  onToggleSavePost: (id: string) => void;
  followedAuthors: string[];
  onToggleFollow: (author: string, authorId?: string) => void;
}) {
  const [section, setSection] = useState<"pets" | "adoption">("pets");
  const [sort, setSort] = useState<"created" | "replied">("created");
  const [topicFilter, setTopicFilter] = useState("热门");
  const [likedPosts, setLikedPosts] = useState<string[]>([]);
  const posts = [
    {
      id: "p1",
      section: "pets",
      author: "西湖散步的人",
      avatar: "西",
      time: "12 分钟前",
      title: "今天遇见的那只橘猫",
      text: "今天的棉花糖比平时更靠近人一点，在阳光下打了个很长的哈欠。希望它这个夏天也能被温柔地记住。",
      tag: "#今日相遇",
      lastReply: 5,
      comments: 18,
      likes: 46,
      portrait: "community-photo-cat",
      image: undefined,
      card: cards[0],
    },
    {
      id: "p2",
      section: "pets",
      author: "街角动物观察员",
      avatar: "观",
      time: "1 小时前",
      title: "树荫下等人的豆包",
      text: "豆包今天在树荫下等人路过。上传一张照片，看看有没有附近的朋友也遇见过它。",
      tag: "#城市里的朋友",
      lastReply: 28,
      comments: 9,
      likes: 31,
      portrait: "community-photo-dog",
      image: undefined,
      card:
        cards.find((card) => card.species === "dog") ?? cards[1] ?? cards[0],
    },
    {
      id: "p3",
      section: "adoption",
      author: "等待新家的小满",
      avatar: "满",
      time: "刚刚",
      title: "为亲人的三花小满寻找领养家庭",
      text: "小满已完成基础健康检查，性格亲人。希望找到愿意长期陪伴、并能接受领养沟通的新家庭。",
      tag: "#领养专区",
      comments: 6,
      likes: 19,
      lastReply: 42,
      portrait: "community-photo-cat",
      image: undefined,
      card: cards[2] ?? cards[0],
    },
    ...userPosts.map((post) => ({
      ...post,
      author: post.author || "林间记录者",
      authorId: post.authorId,
      avatar: "林",
      time: post.createdAt,
      tag: post.section === "adoption" ? "#领养专区" : "#宠友社区",
      comments: 0,
      likes: 0,
      lastReply: 0,
      portrait: "community-photo-cat",
      card: cards.find((card) => card.id === post.cardId) ?? cards[0],
    })),
  ];

  const visiblePosts = posts
    .filter((post) => post.section === section)
    .sort((a, b) => (sort === "replied" ? b.lastReply - a.lastReply : 0));

  return (
    <section className="community-hub">
      <div className="community-heading">
        <img
          className="community-cover-image"
          src="/assets/hero-city-animals-v1.png"
          alt="公园里相伴的城市动物"
        />
        <span className="community-cover-shade" aria-hidden="true" />
        <div>
          <p className="eyebrow">ANIMAL COMMUNITY</p>
          <h1>{section === "pets" ? "宠友社区" : "领养专区"}</h1>
          <p>
            {section === "pets"
              ? "围绕每一次相遇，补充照片与故事。"
              : "发布领养信息，寻找愿意长期陪伴的新家。"}
          </p>
        </div>
        <span className="community-heading-note">记录相遇 · 分享故事</span>
      </div>
      <div className="community-actions">
        <button
          type="button"
          className={section === "pets" ? "active" : ""}
          onClick={() => setSection("pets")}
        >
          <MessageCircle size={18} />
          宠友社区
        </button>
        <button
          type="button"
          className={section === "adoption" ? "active" : ""}
          onClick={() => setSection("adoption")}
        >
          <Camera size={18} />
          领养专区
        </button>
      </div>
      <div className="community-topic-row" aria-label="话题分类">
        {["热门", "最新", "关注"].map((topic) => (
          <button
            className={topicFilter === topic ? "active" : ""}
            type="button"
            key={topic}
            onClick={() => setTopicFilter(topic)}
          >
            {topic}
          </button>
        ))}
      </div>
      <div className="community-feed-head">
        <div>
          <p className="eyebrow">TOPIC FEED</p>
          <h2>{section === "pets" ? "宠友话题" : "领养话题"}</h2>
        </div>
        <div
          className="community-tabs"
          role="tablist"
          aria-label="社区内容范围"
        >
          <button
            className={sort === "created" ? "active" : ""}
            role="tab"
            aria-selected={sort === "created"}
            onClick={() => setSort("created")}
          >
            最新发帖
          </button>
          <button
            className={sort === "replied" ? "active" : ""}
            role="tab"
            aria-selected={sort === "replied"}
            onClick={() => setSort("replied")}
          >
            最新回复
          </button>
        </div>
      </div>
      <button
        type="button"
        className="community-compose"
        onClick={() => onCompose(section)}
      >
        <Plus size={19} />
        发表帖子
      </button>
      <div className="community-feed">
        {visiblePosts.map((post) => {
          const liked = likedPosts.includes(post.id);
          return (
            <article className="community-post" key={post.id}>
              <div className="post-author">
                <span className="author-avatar">{post.avatar}</span>
                <span>
                  <strong>{post.author}</strong>
                  <small>{post.time} · 杭州市</small>
                </span>
                <button
                  type="button"
                  className={
                    followedAuthors.includes(post.author)
                      ? "follow-user is-followed"
                      : "follow-user"
                  }
                  onClick={() => onToggleFollow(post.author, "authorId" in post ? post.authorId : undefined)}
                >
                  {followedAuthors.includes(post.author) ? "已关注" : "关注"}
                </button>
                <button type="button" aria-label="更多操作">
                  <MoreHorizontal size={19} />
                </button>
              </div>
              <button
                type="button"
                className="topic-title"
                onClick={() => onOpenThread(post.id)}
              >
                {post.title}
              </button>
              <p className="post-copy">{post.text}</p>
              <button
                type="button"
                className={`community-photo ${post.portrait}`}
                onClick={() => onOpenThread(post.id)}
                aria-label="查看关联动物卡片"
                style={
                  post.image
                    ? { backgroundImage: `url(${post.image})` }
                    : undefined
                }
              >
                <span className="post-rarity">{post.card?.game.rarity}</span>
              </button>
              <button
                type="button"
                className="linked-animal"
                onClick={() => post.card && onOpen(post.card.id)}
              >
                <span>{post.card?.emoji}</span>
                <span>
                  <small>关联动物卡</small>
                  <strong>{post.card?.name ?? "城市动物"}</strong>
                </span>
                <ChevronRight size={17} />
              </button>
              <span className="post-tag">{post.tag}</span>
              <div className="post-actions">
                <button type="button" onClick={() => onOpenThread(post.id)}>
                  <MessageCircle size={18} />
                  {post.comments}
                </button>
                <button
                  type="button"
                  className={liked ? "liked" : ""}
                  onClick={() =>
                    setLikedPosts((current) =>
                      liked
                        ? current.filter((id) => id !== post.id)
                        : [...current, post.id],
                    )
                  }
                >
                  <Heart size={18} fill={liked ? "currentColor" : "none"} />
                  {post.likes + (liked ? 1 : 0)}
                </button>
                <button type="button" onClick={() => onOpenThread(post.id)}>
                  <Send size={17} />
                  共建
                </button>
                <button
                  type="button"
                  className={savedPostIds.includes(post.id) ? "liked" : ""}
                  onClick={() => onToggleSavePost(post.id)}
                >
                  <Bookmark
                    size={17}
                    fill={
                      savedPostIds.includes(post.id) ? "currentColor" : "none"
                    }
                  />
                  收藏
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function PostComposer({
  section,
  cards,
  recentCardIds,
  onBack,
  onPublish,
  requireLogin,
  showToast,
  sessionToken,
}: {
  section: "pets" | "adoption";
  cards: CatCard[];
  recentCardIds: string[];
  onBack: () => void;
  onPublish: (post: CommunityPost) => void;
  requireLogin: (action: () => void) => void;
  showToast: (text: string) => void;
  sessionToken?: string;
}) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [image, setImage] = useState<string>();
  const [imageFile, setImageFile] = useState<File>();
  const [publishing, setPublishing] = useState(false);
  const [cardId, setCardId] = useState(cards[0]?.id ?? "");
  const [cardQuery, setCardQuery] = useState("");
  const matches = cards.filter((card) => {
    const query = cardQuery.trim().toLowerCase();
    return (
      !query ||
      card.name.toLowerCase().includes(query) ||
      card.code.toLowerCase().includes(query)
    );
  });
  const selectedCard = cards.find((card) => card.id === cardId);
  const submit = () =>
    requireLogin(() => {
      if (!title.trim() || !text.trim()) {
        showToast("请补充帖子标题和正文");
        return;
      }
      if (!sessionToken) { showToast("登录状态已失效，请重新登录"); return; }
      setPublishing(true);
      void (async () => {
        try {
          const imageUrl = imageFile ? await uploadImage(imageFile, sessionToken) : undefined;
          const saved = await createPost(sessionToken, { title: title.trim(), body: text.trim(), imageUrl, cardId, section });
          onPublish({ id: saved.post.id, section, title: saved.post.title, text: saved.post.body, image: saved.post.imageUrl || undefined, cardId: saved.post.cardId || cardId, createdAt: saved.post.createdAt || "刚刚", moderationStatus: saved.post.status });
        } catch { showToast("帖子提交失败，请检查网络后重试"); }
        finally { setPublishing(false); }
      })();
    });
  return (
    <div className="subscreen post-composer-screen">
      <SubHeader title="发表帖子" onBack={onBack} />
      <main className="post-composer-content">
        <section className="composer-story-hero" aria-label="记录相遇">
          <img
            src="/assets/profile-cover-park-v1.png"
            alt="公园里与动物相遇的场景"
          />
          <span aria-hidden="true" />
          <div>
            <p>SHARE AN ENCOUNTER</p>
            <h1>
              记录与 TA 的
              <br />
              美好瞬间
            </h1>
            <small>让每一次相遇，都被温柔地记住。</small>
          </div>
        </section>
        <div className="post-section-pill">
          {section === "pets" ? "宠友社区" : "领养专区"}
        </div>
        <label>
          <span>帖子标题</span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value.slice(0, 40))}
            placeholder={
              section === "pets"
                ? "分享一个想被记住的相遇"
                : "例如：为小满寻找领养家庭"
            }
          />
        </label>
        <label>
          <span>正文</span>
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value.slice(0, 500))}
            placeholder={
              section === "pets"
                ? "记录你的观察、故事或想和大家讨论的问题…"
                : "请说明动物情况、领养期望及后续沟通方式；请勿公开精确地址或私人联系方式。"
            }
          />
          <small>{text.length}/500</small>
        </label>
        <label className="post-image-upload">
          {image ? (
            <img src={image} alt="待发布图片" />
          ) : (
            <ImagePlus size={26} />
          )}
          <span>{image ? "更换图片" : "添加图片"}</span>
          <input
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              if (!file.type.startsWith("image/") || file.size > 10 * 1024 * 1024) { showToast("请上传不超过 10MB 的图片"); return; }
              setImageFile(file);
              setImage(URL.createObjectURL(file));
            }}
          />
        </label>
        <section className="card-picker" aria-label="关联动物卡">
          <span>关联动物卡</span>
          <div className="card-picker-search">
            <Search size={18} />
            <input
              value={cardQuery}
              onChange={(event) =>
                setCardQuery(event.target.value.slice(0, 40))
              }
              placeholder="搜索动物名称或编号"
            />
          </div>
          <div
            className="card-picker-results"
            role="listbox"
            aria-label="搜索结果"
          >
            {matches.length ? (
              matches.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  role="option"
                  aria-selected={card.id === cardId}
                  className={card.id === cardId ? "selected" : ""}
                  onClick={() => setCardId(card.id)}
                >
                  <span>{card.emoji}</span>
                  <span>
                    <strong>{card.name}</strong>
                    <small>{card.code}</small>
                  </span>
                  {recentCardIds.includes(card.id) && <em>最近浏览</em>}
                  {card.id === cardId && <Check size={17} />}
                </button>
              ))
            ) : (
              <p>没有找到匹配的动物卡</p>
            )}
          </div>
          {selectedCard && (
            <small className="card-picker-selected">
              已关联：{selectedCard.name} · {selectedCard.code}
            </small>
          )}
        </section>
        {section === "adoption" && (
          <p className="adoption-compose-note">
            领养内容会进入审核；请勿公开门牌、精确地址、手机号或交易信息。
          </p>
        )}
        <button className="primary-btn wide post-publish" onClick={submit} disabled={publishing}>
          {publishing ? "正在提交审核…" : "发布帖子"}
        </button>
      </main>
    </div>
  );
}

function MyPostsScreen({
  posts,
  cards,
  selectedPostId,
  sessionToken,
  onBack,
  onOpenPost,
  onUpdated,
  onDelete,
  showToast,
}: {
  posts: CommunityPost[];
  cards: CatCard[];
  selectedPostId?: string;
  sessionToken?: string;
  onBack: () => void;
  onOpenPost: (postId: string) => void;
  onUpdated: (post: CommunityPost) => void;
  onDelete: (postId: string) => void;
  showToast: (text: string) => void;
}) {
  return (
    <div className="subscreen my-posts-screen">
      <SubHeader title="我的发布" onBack={onBack} />
      <main className="my-posts-content">
        <header><p className="eyebrow">MY POSTS</p><h1>管理我的帖子</h1><p>审核中的内容仅你可见；未通过后可以修改并重新提交。</p></header>
        {posts.length === 0 ? (
          <div className="my-posts-empty"><MessageCircle size={30} /><strong>还没有发布过帖子</strong><span>可以从社区进入宠友交流或领养专区发布。</span></div>
        ) : (
          <div className="my-posts-list">
            {posts.map((post) => (
              <MyPostItem
                key={post.id}
                post={post}
                cards={cards}
                initiallyEditing={post.id === selectedPostId && post.moderationStatus === "hidden"}
                sessionToken={sessionToken}
                onOpen={() => onOpenPost(post.id)}
                onUpdated={onUpdated}
                onDelete={() => onDelete(post.id)}
                showToast={showToast}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function MyPostItem({
  post,
  cards,
  initiallyEditing,
  sessionToken,
  onOpen,
  onUpdated,
  onDelete,
  showToast,
}: {
  post: CommunityPost;
  cards: CatCard[];
  initiallyEditing: boolean;
  sessionToken?: string;
  onOpen: () => void;
  onUpdated: (post: CommunityPost) => void;
  onDelete: () => void;
  showToast: (text: string) => void;
}) {
  const [editing, setEditing] = useState(initiallyEditing);
  const [title, setTitle] = useState(post.title);
  const [text, setText] = useState(post.text);
  const [section, setSection] = useState<"pets" | "adoption">(post.section);
  const [cardId, setCardId] = useState(post.cardId);
  const [image, setImage] = useState(post.image);
  const [imageFile, setImageFile] = useState<File>();
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (initiallyEditing) setEditing(true); }, [initiallyEditing]);
  const status = post.moderationStatus || "published";
  const submit = async () => {
    if (!sessionToken) { showToast("登录状态已失效，请重新登录"); return; }
    if (!title.trim() || !text.trim()) { showToast("请补充帖子标题和正文"); return; }
    setBusy(true);
    try {
      const imageUrl = imageFile ? await uploadImage(imageFile, sessionToken) : image;
      const saved = await resubmitPost(sessionToken, post.id, { title: title.trim(), body: text.trim(), imageUrl, cardId: cardId || null, section });
      onUpdated({ ...toCommunityPost(saved.post), isMine: true, author: post.author, authorId: post.authorId });
      setEditing(false);
      showToast("帖子已重新提交审核");
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      showToast(message === "card-not-published" ? "关联动物卡尚未公开，请选择其他卡片" : "重新提交失败，请稍后重试");
    } finally { setBusy(false); }
  };
  return (
    <article className={`my-post-item status-${status} ${initiallyEditing ? "is-targeted" : ""}`}>
      <div className="my-post-status-row">
        <span className={`my-post-status ${status}`}>{status === "hidden" ? "未通过" : status === "pending" ? "审核中" : "已发布"}</span>
        <small>{post.section === "adoption" ? "领养专区" : "宠友社区"} · {post.createdAt}</small>
        <button type="button" onClick={onDelete} aria-label="删除帖子"><Trash2 size={15} /></button>
      </div>
      {!editing ? (
        <>
          <button className="my-post-summary" type="button" onClick={status === "published" ? onOpen : undefined}>
            {post.image && <img src={post.image} alt="帖子图片" />}
            <span><strong>{post.title}</strong><p>{post.text}</p></span>
            {status === "published" && <ChevronRight size={17} />}
          </button>
          {status === "pending" && <p className="my-post-review-note"><ShieldCheck size={15} />正在审核，通过后会自动出现在社区。</p>}
          {status === "hidden" && (
            <div className="my-post-rejection"><AlertTriangle size={16} /><span><strong>未通过原因</strong><p>{post.moderationReason || "请调整内容后重新提交审核。"}</p></span></div>
          )}
          {status === "hidden" && <button className="my-post-edit-button" type="button" onClick={() => setEditing(true)}>修改后重新提交</button>}
        </>
      ) : (
        <div className="my-post-edit-form">
          <label>发布分区<select value={section} onChange={(event) => setSection(event.target.value as "pets" | "adoption")}><option value="pets">宠友社区</option><option value="adoption">领养专区</option></select></label>
          <label>帖子标题<input value={title} maxLength={80} onChange={(event) => setTitle(event.target.value)} /></label>
          <label>正文<textarea value={text} maxLength={500} onChange={(event) => setText(event.target.value)} /><small>{text.length}/500</small></label>
          <label className="my-post-image-picker">帖子图片{image && <img src={image} alt="帖子图片预览" />}<span>{image ? "更换图片" : "添加图片"}</span><input type="file" accept="image/*" onChange={(event) => { const file = event.target.files?.[0]; if (!file) return; if (!file.type.startsWith("image/") || file.size > 10 * 1024 * 1024) { showToast("请上传不超过 10MB 的图片"); return; } setImageFile(file); setImage(URL.createObjectURL(file)); }} /></label>
          <label>关联动物卡<select value={cardId} onChange={(event) => setCardId(event.target.value)}><option value="">不关联动物卡</option>{cards.map((card) => <option key={card.id} value={card.id}>{card.name} · {card.code}</option>)}</select></label>
          <div className="my-post-edit-actions"><button type="button" onClick={() => setEditing(false)}>取消</button><button className="primary" type="button" disabled={busy} onClick={() => void submit()}>{busy ? "提交中…" : "重新提交审核"}</button></div>
        </div>
      )}
    </article>
  );
}

function FollowingPage({ authors }: { authors: string[] }) {
  return (
    <section className="following-page">
      <p className="eyebrow">FOLLOWING</p>
      <h1>我的关注</h1>
      <p>查看你关注的宠友及其后续动态。</p>
      {authors.length === 0 ? (
        <div className="following-empty">
          <UserRoundCheck size={26} />
          <strong>还没有关注的宠友</strong>
          <span>在社区的话题作者栏点击“关注”，他们的新动态会出现在这里。</span>
        </div>
      ) : (
        <div className="following-list">
          {authors.map((author) => (
            <article className="following-user" key={author}>
              <span>{author.slice(0, 1)}</span>
              <div>
                <strong>{author}</strong>
                <small>最近发布了新的城市动物话题</small>
              </div>
              <UserRoundCheck size={18} />
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ThreadScreen({
  threadId,
  cards,
  userPosts,
  replies,
  onBack,
  onOpenAnimal,
  onReply,
  requireLogin,
  showToast,
  sessionToken,
  currentUserId,
  onDeletePost,
  onDeleteReply,
}: {
  threadId: string;
  cards: CatCard[];
  userPosts: CommunityPost[];
  replies: Comment[];
  onBack: () => void;
  onOpenAnimal: (id: string) => void;
  onReply: (reply: Comment) => void;
  requireLogin: (action: () => void) => void;
  showToast: (text: string) => void;
  sessionToken?: string;
  currentUserId?: string;
  onDeletePost: (postId: string) => void;
  onDeleteReply: (commentId: string) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState("");
  const [image, setImage] = useState<string>();
  const [imageFile, setImageFile] = useState<File>();
  const [remoteReplies, setRemoteReplies] = useState<Comment[]>([]);
  const isDogThread = threadId === "p2";
  const isAdoptionThread = threadId === "p3";
  const userPost = userPosts.find((post) => post.id === threadId);
  const card = isAdoptionThread
    ? (cards[2] ?? cards[0])
    : userPost
      ? (cards.find((item) => item.id === userPost.cardId) ?? cards[0])
      : isDogThread
        ? (cards.find((item) => item.species === "dog") ?? cards[0])
        : cards[0];
  const thread = userPost
    ? {
        author: userPost.author || "林间记录者",
        avatar: (userPost.author || "林").slice(0, 1),
        time: userPost.createdAt,
        title: userPost.title,
        text: userPost.text,
        photo: "community-photo-cat",
        image: userPost.image,
      }
    : isAdoptionThread
      ? {
          author: "等待新家的小满",
          avatar: "满",
          time: "刚刚",
          title: "为亲人的三花小满寻找领养家庭",
          text: "小满已完成基础健康检查，性格亲人。希望找到愿意长期陪伴、并能接受领养沟通的新家庭。",
          photo: "community-photo-cat",
        }
      : isDogThread
        ? {
            author: "街角动物观察员",
            avatar: "观",
            time: "1 小时前",
            title: "树荫下等人的豆包",
            text: "豆包今天在树荫下等人路过。上传一张照片，看看有没有附近的朋友也遇见过它。",
            photo: "community-photo-dog",
          }
        : {
            author: "西湖散步的人",
            avatar: "西",
            time: "12 分钟前",
            title: "今天遇见的那只橘猫",
            text: "今天的棉花糖比平时更靠近人一点，在阳光下打了个很长的哈欠。希望它这个夏天也能被温柔地记住。",
            photo: "community-photo-cat",
          };
  const seedReplies: Comment[] = [
    {
      id: `${threadId}-seed-1`,
      catId: threadId,
      author: "晚风记录者",
      avatar: "晚",
      text: "我也遇见过它，今天看起来精神很好。谢谢你留下这张照片。",
      createdAt: "8 分钟前",
      status: "published",
    },
    {
      id: `${threadId}-seed-2`,
      catId: threadId,
      author: "小区里的朋友",
      avatar: "邻",
      text: "补充一张傍晚的相遇照，它很喜欢在这里停一会儿。",
      image: "/assets/animal-portraits-grid-v1.png",
      createdAt: "3 分钟前",
      status: "published",
    },
  ];
  const publishedReplies = replies.filter(
    (reply) => reply.status === "published",
  );
  useEffect(() => {
    void fetchReplies(threadId).then(({ comments: items }) => setRemoteReplies(items.map((item) => ({ id: item.id, catId: item.targetId, author: item.author, avatar: item.author.slice(0, 1), text: item.text, image: item.imageUrl, createdAt: "刚刚", status: "published", isMine: item.authorId === currentUserId })))).catch(() => setRemoteReplies([]));
  }, [currentUserId, threadId]);
  const submit = () =>
    requireLogin(() => {
      if (!draft.trim() && !image) {
        showToast("写下你的回复，或添加一张照片吧");
        return;
      }
      if (!sessionToken) { showToast("登录状态已失效，请重新登录"); return; }
      void (async () => {
        try {
          const imageUrl = imageFile ? await uploadImage(imageFile, sessionToken) : undefined;
          const saved = await createReply(sessionToken, { targetId: threadId, text: draft.trim() || "图片回复", imageUrl });
          onReply({ id: saved.comment.id, catId: threadId, author: "我", avatar: "我", text: draft.trim() || "图片回复", image: imageUrl, createdAt: "刚刚", status: "pending", isMine: true });
          setDraft(""); setImage(undefined); setImageFile(undefined);
        } catch { showToast("回复提交失败，请稍后重试"); }
      })();
    });

  return (
    <div className="subscreen thread-screen">
      <SubHeader
        title="帖子详情"
        onBack={onBack}
        right={userPost?.authorId === currentUserId ? (
          <button className="icon-btn danger-icon-btn" type="button" onClick={() => onDeletePost(threadId)} aria-label="删除帖子">
            <Trash2 size={19} />
          </button>
        ) : (
          <button className="icon-btn" aria-label="更多操作">
            <MoreHorizontal size={21} />
          </button>
        )}
      />
      <main className="thread-content">
        <article className="thread-original">
          <div className="post-author">
            <span className="author-avatar">{thread.avatar}</span>
            <span>
              <strong>{thread.author}</strong>
              <small>{thread.time} · 杭州市</small>
            </span>
          </div>
          <h1>{thread.title}</h1>
          <p>{thread.text}</p>
          <div
            className={`thread-photo ${thread.photo}`}
            style={
              thread.image
                ? { backgroundImage: `url(${thread.image})` }
                : undefined
            }
          />
          <button
            className="thread-linked-card"
            type="button"
            onClick={() => card && onOpenAnimal(card.id)}
          >
            <span>{card?.emoji}</span>
            <span>
              <small>关联动物卡</small>
              <strong>{card?.name}</strong>
            </span>
            <ChevronRight size={18} />
          </button>
        </article>
        <section className="thread-replies">
          <div className="thread-replies-head">
            <div>
              <p className="eyebrow">REPLIES</p>
              <h2>全部回帖</h2>
            </div>
            <span>{seedReplies.length + publishedReplies.length + remoteReplies.length} 条</span>
          </div>
          {[...seedReplies, ...remoteReplies, ...publishedReplies].map((reply, index) => (
            <article className="thread-reply" key={reply.id}>
              <span className="reply-floor">{index + 1} 楼</span>
              <CommentItem
                comment={reply}
                onDelete={reply.isMine ? async () => {
                  if (await onDeleteReply(reply.id)) {
                    setRemoteReplies((items) => items.filter((item) => item.id !== reply.id));
                  }
                } : undefined}
              />
            </article>
          ))}
          {replies.some((reply) => reply.status === "pending") && (
            <div className="pending-note">
              <ShieldCheck size={16} />
              你的图文回复正在审核，通过后会显示在回帖中。
            </div>
          )}
        </section>
      </main>
      <div className="comment-composer">
        <label className="attach-button">
          <ImagePlus size={19} />
          <input
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              if (!file.type.startsWith("image/") || file.size > 10 * 1024 * 1024) { showToast("请上传不超过 10MB 的图片"); return; }
              setImageFile(file);
              setImage(URL.createObjectURL(file));
            }}
          />
        </label>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, 200))}
          placeholder={image ? "为图片写一句说明…" : "写下你的图文回复…"}
        />
        <button className="send-button" onClick={submit} aria-label="发布回复">
          <Send size={19} />
        </button>
        {image && (
          <div className="attached-preview">
            <img src={image} alt="待发布的回复图片" />
            <button onClick={() => setImage(undefined)}>
              <X size={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CardTile({
  card,
  onOpen,
  isSaved,
  onToggleSave,
}: {
  card: CatCard;
  onOpen: () => void;
  isSaved: boolean;
  onToggleSave: () => void;
}) {
  const isPublished = !card.moderationStatus || card.moderationStatus === "published";
  const demoPortrait = {
    mianhua: "portrait-cat-a",
    doubao: "portrait-dog-a",
    hei: "portrait-cat-b",
    hua: "portrait-dog-b",
  }[card.id];

  return (
    <article className="cat-card" onClick={onOpen}>
      <div className="tile-visual-wrap">
        {demoPortrait ? (
          <div
            className={`demo-animal-photo ${demoPortrait}`}
            aria-label={`${card.name}的展示照片`}
          />
        ) : (
          <CatVisual card={card} />
        )}
        <span className={`tile-rarity rarity-${card.game.rarity}`}>
          {card.game.rarity}
        </span>
        {!isPublished && (
          <span className={`tile-review-status ${card.moderationStatus}`}>
            {card.moderationStatus === "hidden" ? "未通过" : "审核中"}
          </span>
        )}
      </div>
      <div className="cat-card-body">
        <div className="card-title-row">
          <h3>{card.name}</h3>
          {isPublished && (
            <button
              className={isSaved ? "card-save is-saved" : "card-save"}
              type="button"
              aria-label={isSaved ? "取消收藏动物卡" : "收藏动物卡"}
              onClick={(event) => {
                event.stopPropagation();
                onToggleSave();
              }}
            >
              <Bookmark size={15} fill={isSaved ? "currentColor" : "none"} />
            </button>
          )}
        </div>
        <p>
          <MapPin size={13} />
          <AreaLabel area={card.area} />
        </p>
        <div className="tile-game-row">
          <span>探索属性已设定</span>
        </div>
        <span className="card-code">{card.code}</span>
      </div>
    </article>
  );
}

function EmptyAtlas({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="empty-state">
      <span>🐾</span>
      <h2>从第一只猫开始</h2>
      <p>把一次街角相遇，变成你的第一张图鉴卡。</p>
      <button className="primary-btn" onClick={onCreate}>
        记录一只猫
      </button>
    </div>
  );
}

function NavItem({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button className={`nav-item ${active ? "active" : ""}`} onClick={onClick}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

function AuthSheet({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (session: AuthSession) => void;
}) {
  const [mode, setMode] = useState<"register" | "login">("register");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const submit = async () => {
    setBusy(true);
    try { onSuccess(mode === "register" ? await registerAccount(username, password) : await loginWithPassword(username, password)); }
    catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      setHint(message === "too-many-attempts" ? "尝试次数过多，请稍后再试" : mode === "register" ? "账号不可用，或密码不符合要求" : "账号或密码不正确");
    }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop">
      <section className="auth-sheet">
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>
        <div className="auth-cat">🐾</div>
        <h2>{mode === "register" ? "创建你的账号" : "欢迎回来"}</h2>
        <p>发布动物卡、评论或图片回复前，需要登录账号。</p>
        <label>
          账号
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value.replace(/\s/g, "").slice(0, 24))}
            placeholder="3–24 位英文、数字、_ 或 -"
            autoCapitalize="none"
          />
        </label>
        <label>密码<input value={password} type="password" onChange={(event) => setPassword(event.target.value.slice(0, 128))} placeholder="至少 8 位" autoComplete={mode === "register" ? "new-password" : "current-password"} /></label>
        <button className="primary-btn wide" disabled={busy || username.length < 3 || password.length < 8} onClick={submit}>
          {busy ? "处理中…" : mode === "register" ? "注册并进入" : "登录"}
        </button>
        {hint && <small>{hint}</small>}
        <small>
          {mode === "register" ? "已有账号？" : "第一次使用？"} <button className="text-action" onClick={() => { setMode(mode === "register" ? "login" : "register"); setHint(""); }}>{mode === "register" ? "去登录" : "创建账号"}</button><br />
          仅公开你的昵称和头像；密码会加密保存，我们不会公开你的定位坐标。
        </small>
      </section>
    </div>
  );
}

function AccountSecuritySheet({
  token,
  onClose,
  onPasswordChanged,
  onAllSessionsRevoked,
}: {
  token: string;
  onClose: () => void;
  onPasswordChanged: () => void;
  onAllSessionsRevoked: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState<"password" | "sessions">();
  const [hint, setHint] = useState("");
  const submitPassword = async () => {
    if (newPassword !== confirmPassword) { setHint("两次输入的新密码不一致"); return; }
    setBusy("password"); setHint("");
    try {
      await changePassword(token, currentPassword, newPassword);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      setHint("密码修改成功，其他设备已退出登录");
      onPasswordChanged();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      setHint(message === "current-password-invalid" ? "原密码不正确" : message === "new-password-must-differ" ? "新密码不能与原密码相同" : message === "too-many-attempts" ? "尝试次数过多，请稍后再试" : "密码修改失败，请稍后重试");
    } finally { setBusy(undefined); }
  };
  const signOutEverywhere = async () => {
    if (!window.confirm("这会让当前设备和其他所有设备立即退出登录，确定继续吗？")) return;
    setBusy("sessions"); setHint("");
    try { await revokeAllSessions(token); onAllSessionsRevoked(); }
    catch { setHint("退出失败，请检查网络后重试"); setBusy(undefined); }
  };
  return (
    <div className="modal-backdrop">
      <section className="auth-sheet security-sheet" aria-label="账号安全">
        <button className="close-btn" type="button" onClick={onClose}><X size={20} /></button>
        <div className="auth-cat"><KeyRound size={34} /></div>
        <h2>账号与登录安全</h2>
        <p>修改密码后，当前设备会保留登录，其他设备会自动退出。</p>
        <label>原密码<input value={currentPassword} type="password" autoComplete="current-password" onChange={(event) => setCurrentPassword(event.target.value.slice(0, 128))} /></label>
        <label>新密码<input value={newPassword} type="password" autoComplete="new-password" placeholder="至少 8 位" onChange={(event) => setNewPassword(event.target.value.slice(0, 128))} /></label>
        <label>确认新密码<input value={confirmPassword} type="password" autoComplete="new-password" onChange={(event) => setConfirmPassword(event.target.value.slice(0, 128))} /></label>
        <button className="primary-btn wide" type="button" disabled={Boolean(busy) || currentPassword.length < 8 || newPassword.length < 8 || confirmPassword.length < 8} onClick={() => void submitPassword()}>
          {busy === "password" ? "修改中…" : "修改密码"}
        </button>
        {hint && <small className="security-hint">{hint}</small>}
        <div className="security-session-block">
          <div><LogOut size={18} /><span><strong>退出所有设备</strong><small>适合怀疑账号泄露或忘记退出旧设备时使用。</small></span></div>
          <button type="button" disabled={Boolean(busy)} onClick={() => void signOutEverywhere()}>{busy === "sessions" ? "退出中…" : "立即退出"}</button>
        </div>
      </section>
    </div>
  );
}

function NotificationSheet({
  notifications,
  unreadCount,
  onClose,
  onOpen,
  onReadAll,
}: {
  notifications: AppNotification[];
  unreadCount: number;
  onClose: () => void;
  onOpen: (notification: AppNotification) => void;
  onReadAll: () => void;
}) {
  const formatTime = (value: string) => {
    const timestamp = new Date(value);
    if (Number.isNaN(timestamp.getTime())) return "刚刚";
    return timestamp.toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  return (
    <div className="modal-backdrop">
      <section className="notification-sheet" aria-label="通知中心">
        <div className="notification-sheet-header">
          <div><p className="eyebrow">NOTIFICATIONS</p><h2>通知</h2></div>
          <div>
            {unreadCount > 0 && <button className="notification-read-all" type="button" onClick={onReadAll}>全部已读</button>}
            <button className="close-btn" type="button" onClick={onClose}><X size={20} /></button>
          </div>
        </div>
        <div className="notification-list">
          {notifications.length === 0 ? (
            <div className="notification-empty"><Bell size={30} /><strong>暂时没有新通知</strong><p>动物卡、帖子和评论的审核结果会显示在这里。</p></div>
          ) : notifications.map((notification) => {
            const rejected = notification.type === "moderation_hidden";
            return (
              <button className={`notification-item ${notification.readAt ? "is-read" : "is-unread"}`} type="button" key={notification.id} onClick={() => onOpen(notification)}>
                <span className={`notification-state ${rejected ? "rejected" : "approved"}`}>{rejected ? <AlertTriangle size={18} /> : <Check size={18} />}</span>
                <span className="notification-copy">
                  <span><strong>{notification.title}</strong><time>{formatTime(notification.createdAt)}</time></span>
                  <p>{notification.body}</p>
                  {notification.targetType === "card" && <small>{rejected ? "点击进入卡片修改" : "点击查看动物卡"} <ChevronRight size={13} /></small>}
                </span>
                {!notification.readAt && <i aria-label="未读" />}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function ProfileSetupSheet({
  profile,
  onSave,
}: {
  profile: PublicProfile;
  onSave: (profile: PublicProfile, avatarFile?: File) => void | Promise<void>;
}) {
  const [nickname, setNickname] = useState(profile.nickname === "新朋友" ? "" : profile.nickname);
  const [bio, setBio] = useState(profile.bio);
  const [city, setCity] = useState(profile.city);
  const [avatar, setAvatar] = useState(profile.avatar);
  const [avatarFile, setAvatarFile] = useState<File>();
  const chooseAvatar = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > 10 * 1024 * 1024) return;
    setAvatarFile(file);
    setAvatar(URL.createObjectURL(file));
  };
  const saveProfile = () => {
    const cleanedName = nickname.trim();
    if (!cleanedName) return;
    void onSave({
      nickname: cleanedName.slice(0, 14),
      bio: bio.trim().slice(0, 42) || "记录与城市动物的相遇。",
      city: city.trim().slice(0, 12) || "杭州市",
      joinedAt: profile.joinedAt,
      avatar: avatarFile ? undefined : avatar,
    }, avatarFile);
  };
  return (
    <div className="modal-backdrop profile-setup-backdrop">
      <section className="profile-setup-sheet" aria-label="完善公开资料">
        <p className="eyebrow">WELCOME TO THE ATLAS</p>
        <h2>设置你的公开资料</h2>
        <p>昵称和头像会展示在你的帖子、评论与个人主页中。</p>
        <label className="profile-avatar-picker">
          <input type="file" accept="image/*" onChange={chooseAvatar} />
          <span className="profile-avatar-picker-preview">
            {avatar ? <img src={avatar} alt="头像预览" /> : nickname.slice(0, 1) || "我"}
          </span>
          <strong>上传头像</strong>
          <small>可选</small>
        </label>
        <label>
          昵称
          <input
            value={nickname}
            onChange={(event) => setNickname(event.target.value.slice(0, 14))}
            placeholder="例如：西湖散步的人"
            maxLength={14}
          />
        </label>
        <label>
          个人简介
          <input
            value={bio}
            onChange={(event) => setBio(event.target.value.slice(0, 42))}
            placeholder="用一句话介绍你与动物的故事"
            maxLength={42}
          />
        </label>
        <label>
          所在城市
          <input
            value={city}
            onChange={(event) => setCity(event.target.value.slice(0, 12))}
            placeholder="例如：杭州市"
            maxLength={12}
          />
        </label>
        <button className="primary-btn wide" disabled={!nickname.trim()} onClick={saveProfile}>
          完成并进入图鉴
        </button>
      </section>
    </div>
  );
}

function Profile({
  signedIn,
  level,
  onSignIn,
  profile,
  onEditProfile,
  cards,
  onOpenCard,
  onOpenAtlas,
  onOpenChallenges,
  onOpenSecurity,
  onOpenSafety,
  onDeleteAccount,
}: {
  signedIn: boolean;
  level: number;
  onSignIn: () => void;
  profile: PublicProfile;
  onEditProfile: () => void;
  cards: CatCard[];
  onOpenCard: (id: string) => void;
  onOpenAtlas: () => void;
  onOpenChallenges: () => void;
  onOpenSecurity: () => void;
  onOpenSafety: () => void;
  onDeleteAccount: () => void;
}) {
  const profileCards = cards.filter((card) => card.isMine).slice(0, 4);
  const publishedProfileCards = cards.filter((card) => card.isMine && (!card.moderationStatus || card.moderationStatus === "published"));
  const profileCoatCount = new Set(publishedProfileCards.map((card) => card.coat)).size;
  const profileAchievementCount = getChallengeAchievements(publishedProfileCards).filter((achievement) => achievement.value >= achievement.goal).length;
  const profilePortraits = [
    "/assets/profile-animal-orange-v1.png",
    "/assets/profile-animal-graywhite-v1.png",
    "/assets/profile-animal-charcoal-v1.png",
    "/assets/profile-animal-shiba-v1.png",
  ];
  return (
    <section className="profile profile-rebuilt">
      <div className="profile-masthead">
        <img
          className="profile-cover-photo"
          src="/assets/profile-cover-park-v1.png"
          alt="城市动物记录者与宠物在公园中"
        />
        <div className="profile-cover-shade" />
        <div className="profile-cover-copy">
          <p className="eyebrow">MY ANIMAL STORY</p>
          <div className="profile-identity">
            <div className="profile-avatar profile-photo-avatar">
              {signedIn ? (
                profile.avatar ? (
                  <img src={profile.avatar} alt={`${profile.nickname}的头像`} />
                ) : (
                  profile.nickname.slice(0, 1)
                )
              ) : (
                "？"
              )}
            </div>
            <div>
              <div className="profile-name-line">
                <h1>{signedIn ? profile.nickname : "还没有登录"}</h1>
                {signedIn && <em>Lv.{level}</em>}
              </div>
              <p>
                {signedIn
                  ? profile.bio
                  : "登录后记录相遇，收藏每一张动物卡。"}
              </p>
              {signedIn && (
                <span className="profile-identity-meta">
                  <MapPin size={12} /> {profile.city} <i /> 加入 {profile.joinedAt}
                </span>
              )}
            </div>
          </div>
          {signedIn && (
            <button className="profile-edit-btn" type="button" onClick={onEditProfile}>
              编辑资料
            </button>
          )}
          {!signedIn && (
            <button className="profile-login-btn" onClick={onSignIn}>
              账号登录
            </button>
          )}
        </div>
        <div className="profile-overview">
          <div className="profile-stats" aria-label="个人图鉴数据">
            <button type="button" onClick={onOpenAtlas}>
              <strong>{publishedProfileCards.length}</strong>
              <span>图鉴卡</span>
            </button>
            <button type="button" onClick={onOpenChallenges}>
              <strong>{profileCoatCount}</strong>
              <span>外观</span>
            </button>
            <button type="button" onClick={onOpenChallenges}>
              <strong>{profileAchievementCount}</strong>
              <span>成就</span>
            </button>
          </div>
        </div>
      </div>
      <section className="profile-shelf" aria-label="我的收藏动物">
        <div className="profile-shelf-heading">
          <div>
            <p className="eyebrow">MY COLLECTION · ANIMALS</p>
            <h2>我的图鉴</h2>
          </div>
          <button type="button" onClick={onOpenAtlas}>
            查看全部 <ChevronRight size={15} />
          </button>
        </div>
        <div className="profile-animal-strip">
          {profileCards.map((card, index) => (
            <button
              key={card.id}
              type="button"
              className="profile-animal-tile"
              onClick={() => onOpenCard(card.id)}
            >
              <img
                className="profile-animal-image"
                src={card.image || profilePortraits[index]}
                alt={`${card.name}的展示照片`}
              />
              <strong>{card.name}</strong>
              <small>{card.coat}</small>
              {card.moderationStatus && card.moderationStatus !== "published" && (
                <span className={`profile-review-status ${card.moderationStatus}`}>
                  {card.moderationStatus === "hidden" ? "未通过" : "审核中"}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>
      <section className="profile-shelf profile-post-shelf" aria-label="图鉴挑战">
        <div className="profile-shelf-heading">
          <div>
            <p className="eyebrow">MY CHALLENGES</p>
            <h2>图鉴挑战</h2>
          </div>
          <button type="button" onClick={onOpenChallenges}>
            查看全部 <ChevronRight size={15} />
          </button>
        </div>
        <button className="profile-post-preview" type="button" onClick={onOpenChallenges}>
          <img src="/assets/hero-city-animals-v1.png" alt="一只被记录的城市动物" />
          <span>
            <strong>继续点亮主题图鉴</strong>
            <small>完成挑战，解锁个人成就徽章</small>
          </span>
        </button>
      </section>
      <p className="profile-section-label">账号与安全</p>
      <div className="profile-menu">
        <button type="button" onClick={onOpenSecurity}>
          <ShieldCheck size={19} />
          隐私与安全
          <ChevronRight size={17} />
        </button>
        <button type="button" onClick={onOpenSafety}>
          <Leaf size={19} />
          AI 内容与动物安全说明
          <ChevronRight size={17} />
        </button>
        <button type="button" className="profile-menu-danger" onClick={onDeleteAccount}>
          <Trash2 size={19} />
          数据删除与账户注销
          <ChevronRight size={17} />
        </button>
      </div>
    </section>
  );
}

function CreateScreen({
  onBack,
  onCreated,
  requireLogin,
  showToast,
  sessionToken,
}: {
  onBack: () => void;
  onCreated: (card: CatCard) => void;
  requireLogin: (action: () => void) => void;
  showToast: (text: string) => void;
  sessionToken?: string;
}) {
  const [image, setImage] = useState<string>();
  const [imageFile, setImageFile] = useState<File>();
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>();
  const [species, setSpecies] = useState<CatCard["species"]>("cat");
  const [name, setName] = useState("");
  const [area, setArea] = useState<AreaScope>(suggestedArea);
  const [game, setGame] = useState<CatCard["game"]>({
    rarity: "SR",
    courage: 3,
    affinity: 3,
    curiosity: 3,
    stealth: 3,
    appetite: 3,
    speed: 3,
    agility: 3,
    intelligence: 3,
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [visualDraft, setVisualDraft] = useState<{
    imageUrl: string;
    species: CatCard["species"];
    coat: string;
    feature: string;
    observation: string;
  }>();
  const handleImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("请选择图片文件");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast("图片不能超过 10MB");
      return;
    }
    setImageFile(file);
    setImage(URL.createObjectURL(file));
    setUploadedImageUrl(undefined);
    setVisualDraft(undefined);
  };
  const recommendArea = async () => {
    try {
      const permission = await Geolocation.requestPermissions({
        permissions: ["location"],
      });
      if (permission.location !== "granted") {
        showToast("未授权定位，请手动选择行政区");
        return;
      }
      // 坐标仅在此设备会话内用于行政区反查。正式服务只能接收反查后的行政区代码，不能接收坐标。
      await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 300000,
      });
      setArea(suggestedArea);
      showToast("已推荐行政区，坐标不会保存");
    } catch {
      showToast("未能获取定位，请手动选择行政区");
    }
  };
  const generate = () =>
    requireLogin(() => {
      if (!image || !imageFile) {
        showToast(`请先上传一张${species === "dog" ? "狗狗" : "猫咪"}照片`);
        return;
      }
      setAnalyzing(true);
      if (!sessionToken) { showToast("登录状态已失效，请重新登录"); return; }
      void (async () => {
        try {
          const uploadedImage = uploadedImageUrl || await uploadImage(imageFile, sessionToken);
          setUploadedImageUrl(uploadedImage);
          const visual = await analyzeAnimal(uploadedImage, sessionToken);
          const identifiedSpecies = visual.species;
          setSpecies(visual.species);
          setVisualDraft({ imageUrl: uploadedImage, species: identifiedSpecies, coat: visual.coat, feature: visual.feature, observation: visual.observation });
        } catch (caught) {
          const message = caught instanceof Error && caught.message === "animal-vision-not-configured" ? "AI 视觉服务尚未配置，请联系运营" : caught instanceof Error && caught.message === "network-timeout" ? "网络超时，请重试" : caught instanceof Error && caught.message === "content-blocked" ? "内容未通过安全审核，请更换图片或修改描述" : "记录保存失败，请稍后重试";
          showToast(message);
        } finally {
          setAnalyzing(false);
        }
      })();
    });
  const confirmAndCreate = () => requireLogin(() => {
    if (!visualDraft || !imageFile || !sessionToken) return;
    if (!visualDraft.coat.trim() || !visualDraft.feature.trim() || !visualDraft.observation.trim()) { showToast("请确认并补全 AI 外观观察"); return; }
    setAnalyzing(true);
    void (async () => {
      try {
        let cutoutImage: string | undefined; let cutoutStatus: CatCard["cutoutStatus"] = "fallback";
        try { cutoutImage = await requestCatCutout(visualDraft.imageUrl, sessionToken); cutoutStatus = "processed"; }
        catch { showToast("主体抠图服务处理中，当前先展示原图预览"); }
        const draft = { ...createDemoCard({ species: visualDraft.species, name, image: visualDraft.imageUrl, cutoutImage, cutoutStatus, area, game }), coat: visualDraft.coat, feature: visualDraft.feature, observation: `AI 辅助观察（用户确认）：${visualDraft.observation}` };
        const saved = await createCard(draft, sessionToken); onCreated(saved.card);
      } catch { showToast("记录保存失败，请稍后重试"); }
      finally { setAnalyzing(false); }
    })();
  });
  return (
    <div className="subscreen">
      <SubHeader title={`记录一只${species === "dog" ? "狗" : "猫"}`} onBack={onBack} />
      <main className="create-content">
        <section className="privacy-note">
          <ShieldCheck size={18} />
          <span>照片将移除位置等 EXIF 信息；仅保存行政区域，不保存坐标。</span>
        </section>
        <div className="species-picker" role="tablist" aria-label="选择动物类型">
          <button
            type="button"
            role="tab"
            aria-selected={species === "cat"}
            className={species === "cat" ? "active" : ""}
            onClick={() => {
              setSpecies("cat");
            }}
          >
            🐈 猫猫
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={species === "dog"}
            className={species === "dog" ? "active" : ""}
            onClick={() => {
              setSpecies("dog");
            }}
          >
            🐶 狗狗
          </button>
        </div>
        <label className={`photo-picker ${image ? "has-image" : ""}`}>
          {image ? (
            <img src={image} alt={`待分析的${species === "dog" ? "狗狗" : "猫咪"}`} />
          ) : (
            <>
              <ImagePlus size={31} />
              <strong>拍照或选择{species === "dog" ? "狗狗" : "猫咪"}照片</strong>
              <span>请避免拍入人脸、车牌和住址</span>
            </>
          )}
          <input type="file" accept="image/*" onChange={handleImage} />
        </label>
        {visualDraft ? (
          <section className="ai-result-confirmation" aria-live="polite">
            <div className="ai-result-heading"><div><p>AI RESULT REVIEW</p><h2>确认这张动物卡</h2></div><span>可修改后再发布</span></div>
            <div className="ai-result-grid">
              <label>给它的名字<input value={name} onChange={(event) => setName(event.target.value.slice(0, 20))} placeholder={visualDraft.species === "dog" ? "例如：公园门口的豆包" : "例如：便利店门口的小橘"} /></label>
              <label>动物类型<select value={visualDraft.species} onChange={(event) => { const nextSpecies = event.target.value as CatCard["species"]; setVisualDraft({ ...visualDraft, species: nextSpecies }); setSpecies(nextSpecies); }}><option value="cat">猫猫</option><option value="dog">狗狗</option></select></label>
              <label>AI 花色观察<input value={visualDraft.coat} onChange={(event) => setVisualDraft({ ...visualDraft, coat: event.target.value.slice(0, 20) })} /></label>
              <label>AI 可见特征<input value={visualDraft.feature} onChange={(event) => setVisualDraft({ ...visualDraft, feature: event.target.value.slice(0, 28) })} /></label>
              <label>AI 趣味观察<textarea value={visualDraft.observation} onChange={(event) => setVisualDraft({ ...visualDraft, observation: event.target.value.slice(0, 70) })} /></label>
              <label>遇见的区域（仅图鉴归属）<div className="ai-area-grid"><select value={area.city} onChange={(event) => setArea({ ...area, city: event.target.value })}><option>杭州市</option><option>上海市</option><option>深圳市</option></select><select value={area.district} onChange={(event) => setArea({ ...area, district: event.target.value })}><option>西湖区</option><option>上城区</option><option>余杭区</option></select><select value={area.street} onChange={(event) => setArea({ ...area, street: event.target.value })}><option>文新街道</option><option>翠苑街道</option><option>灵隐街道</option></select></div></label>
            </div>
            <p>以上为基于照片的 AI 外观观察，仅供参考；不构成品种、年龄或健康判断。</p>
          </section>
        ) : (
        <section className="form-section">
          <label>
            给它一个名字（可选）
            <input
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 20))}
              placeholder={species === "dog" ? "例如：公园门口的豆包" : "例如：便利店门口的小橘"}
            />
          </label>
          <div className="field-label">
            遇见的区域 <span>仅用于图鉴归属</span>
          </div>
          <div className="area-display">
            <MapPin size={18} />
            <span>
              <AreaLabel area={area} detailed />
            </span>
            <button onClick={recommendArea}>自动推荐</button>
          </div>
          <div className="select-grid">
            <select
              value={area.city}
              onChange={(event) =>
                setArea({ ...area, city: event.target.value })
              }
            >
              <option>杭州市</option>
              <option>上海市</option>
              <option>深圳市</option>
            </select>
            <select
              value={area.district}
              onChange={(event) =>
                setArea({ ...area, district: event.target.value })
              }
            >
              <option>西湖区</option>
              <option>上城区</option>
              <option>余杭区</option>
            </select>
            <select
              value={area.street}
              onChange={(event) =>
                setArea({ ...area, street: event.target.value })
              }
            >
              <option>文新街道</option>
              <option>翠苑街道</option>
              <option>灵隐街道</option>
            </select>
          </div>
          <small className="area-tip">
            公开卡片仅显示“市 · 区县”，街道不会公开展示。
          </small>
        </section>)}
        <AttributeEditor game={game} onChange={setGame} />
        <button
          className="primary-btn wide generate-btn"
          onClick={visualDraft ? confirmAndCreate : generate}
          disabled={analyzing}
        >
          {analyzing ? (
            <>
              <Sparkles size={19} className="spin" />
              {visualDraft ? "正在保存动物卡…" : "正在分析照片…"}
            </>
          ) : (
            <>
              <Sparkles size={19} />
              {visualDraft ? "确认并生成动物卡" : `分析${species === "dog" ? "狗狗" : "猫咪"}照片`}
            </>
          )}
        </button>
        <p className="ai-note">
          {visualDraft ? "确认后才会生成并提交动物卡。探索属性由上传者自行设定，仅作趣味档案。" : "先分析照片，再由你确认 AI 外观观察；探索属性由上传者自行设定。"}
        </p>
      </main>
    </div>
  );
}

function StarRating({ value }: { value: number }) {
  const normalizedValue = Math.max(0, Math.min(5, value));

  return (
    <span className="star-rating" aria-label={`${normalizedValue.toFixed(1)} 星`}>
      {Array.from({ length: 5 }, (_, index) => {
        const remaining = normalizedValue - index;
        const state = remaining >= 1 ? "full" : remaining >= 0.5 ? "half" : "empty";

        return (
          <span className={`star-rating-star is-${state}`} aria-hidden="true" key={index}>
            {state === "full" ? "★" : "☆"}
          </span>
        );
      })}
    </span>
  );
}

function AttributeEditor({
  game,
  onChange,
}: {
  game: CatCard["game"];
  onChange: (value: CatCard["game"]) => void;
}) {
  const labels = [
    ["courage", "胆量"],
    ["affinity", "亲人度"],
    ["curiosity", "好奇心"],
    ["stealth", "潜行力"],
    ["appetite", "饭量"],
    ["speed", "速度"],
    ["agility", "敏捷"],
    ["intelligence", "智力"],
  ] as const;
  return (
    <section className="attribute-editor">
      <div className="attribute-editor-heading">
        <div>
          <p>EXPLORER PROFILE</p>
          <h2>设置探索属性</h2>
        </div>
        <span>上传者趣味设定</span>
      </div>
      <div className="rarity-picker">
        <span>稀有度</span>
        <strong>生成猫咪卡后由系统评定（UR ＞ SSR ＞ SR ＞ R ＞ N）</strong>
      </div>
      <div className="attribute-controls">
        {labels.map(([key, label]) => (
          <label key={key}>
            <div>
              <span>{label}</span>
              <strong>{game[key].toFixed(1)} 星</strong>
            </div>
            <input
              type="range"
              min="0.5"
              max="5"
              step="0.5"
              value={game[key]}
              onChange={(event) =>
                onChange({ ...game, [key]: Number(event.target.value) })
              }
            />
            <small><StarRating value={game[key]} /></small>
          </label>
        ))}
      </div>
      <p>最低半星，满分五星；这些属性不代表猫咪真实状态。</p>
    </section>
  );
}

type CardLayoutKey =
  | "identity" | "uploader" | "code" | "ai" | "location" | "traits"
  | "heading" | "footnote" | "observation" | "disclaimer"
  | `stat-${number}`
  | `stat-label-${number}`
  | `stat-stars-${number}`;
type CardLayout = Partial<Record<CardLayoutKey, { x: number; y: number }>>;
const sharedCardLayoutStorageKey = "city-animal-card-layout-shared";
const legacyCardLayoutStorageKeys = [
  sharedCardLayoutStorageKey,
  ...rarityOrder.map((rarity) => `city-animal-card-layout-${rarity}`),
];

function loadSharedCardLayout(): CardLayout {
  // Card layout editing has been retired.  Never apply a position that was
  // saved by an earlier preview, otherwise one drag offsets every rarity.
  return {};
}

function clearLegacyCardLayouts() {
  legacyCardLayoutStorageKeys.forEach((key) => window.localStorage.removeItem(key));
}

function MovableCardItem({
  id,
  editing,
  selected,
  layout,
  onSelect,
  onMove,
  children,
  className = "",
  fallbackId,
}: {
  id: CardLayoutKey;
  editing: boolean;
  selected: boolean;
  layout: CardLayout;
  onSelect: (id: CardLayoutKey) => void;
  onMove: (id: CardLayoutKey, x: number, y: number) => void;
  children: React.ReactNode;
  className?: string;
  fallbackId?: CardLayoutKey;
}) {
  const point = layout[id] ?? (fallbackId ? layout[fallbackId] : undefined) ?? { x: 0, y: 0 };
  const dragStart = useRef<{ clientX: number; clientY: number; x: number; y: number } | undefined>(undefined);
  return (
    <div
      className={`card-layout-item ${editing ? "is-editable" : ""} ${selected ? "is-selected" : ""} ${className}`}
      style={{ transform: `translate3d(${point.x}px, ${point.y}px, 0)` }}
      tabIndex={editing ? 0 : undefined}
      onPointerDown={(event) => {
        if (!editing) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStart.current = { clientX: event.clientX, clientY: event.clientY, x: point.x, y: point.y };
        onSelect(id);
      }}
      onPointerMove={(event) => {
        if (!editing || !dragStart.current || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
        event.stopPropagation();
        onMove(id, Math.round(dragStart.current.x + event.clientX - dragStart.current.clientX), Math.round(dragStart.current.y + event.clientY - dragStart.current.clientY));
      }}
      onPointerUp={(event) => {
        if (!editing) return;
        event.stopPropagation();
        dragStart.current = undefined;
      }}
      onKeyDown={(event) => {
        if (!editing || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        const step = event.shiftKey ? 10 : 1;
        onMove(id, point.x + (event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0), point.y + (event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0));
      }}
    >
      {children}
      {editing && selected && <span className="card-layout-coordinates">x {point.x} · y {point.y}</span>}
    </div>
  );
}

function cardTextForDisplay(value: string, fallback: string) {
  // Older cards may have been produced while the vision model returned English.
  // Do not let that unreviewed, overlong output break the fixed card template.
  return /[A-Za-z]{3,}/.test(value) ? fallback : value;
}

function CollectibleCard({ card, editing = false, onEditingChange }: { card: CatCard; editing?: boolean; onEditingChange?: (editing: boolean) => void }) {
  const cardRef = useRef<HTMLElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0, lightX: 50, lightY: 28 });
  const [isTilting, setIsTilting] = useState(false);
  const [selectedLayoutItem, setSelectedLayoutItem] = useState<CardLayoutKey>();
  const [layout, setLayout] = useState<CardLayout>(loadSharedCardLayout);
  const cardCoat = cardTextForDisplay(card.coat, "花色以照片为准");
  const cardFeature = cardTextForDisplay(card.feature, "可见特征以照片为准");
  const cardObservation = cardTextForDisplay(card.observation, "AI 辅助观察（用户确认）：正安静地观察周围。");

  useEffect(() => {
    clearLegacyCardLayouts();
    setLayout({});
    setSelectedLayoutItem(undefined);
  }, [card.id]);

  const moveLayoutItem = (id: CardLayoutKey, x: number, y: number) =>
    setLayout((current) => ({ ...current, [id]: { x, y } }));
  const saveLayout = () => {
    window.localStorage.setItem(sharedCardLayoutStorageKey, JSON.stringify(layout));
    onEditingChange?.(false);
    setSelectedLayoutItem(undefined);
  };
  const resetLayout = () => {
    setLayout({});
    clearLegacyCardLayouts();
    setSelectedLayoutItem(undefined);
  };

  const updateTilt = (clientX: number, clientY: number) => {
    const bounds = cardRef.current?.getBoundingClientRect();
    if (!bounds) return;

    const pointX = Math.min(
      1,
      Math.max(0, (clientX - bounds.left) / bounds.width),
    );
    const pointY = Math.min(
      1,
      Math.max(0, (clientY - bounds.top) / bounds.height),
    );
    setTilt({
      x: (0.5 - pointY) * 14,
      y: (pointX - 0.5) * 24,
      lightX: pointX * 100,
      lightY: pointY * 100,
    });
  };

  const resetTilt = () => {
    setIsTilting(false);
    setTilt({ x: 0, y: 0, lightX: 50, lightY: 28 });
  };

  return (
    <div className="collectible-card-wrap">
      <article
        ref={cardRef}
        data-testid="collectible-card"
        className={`collectible-card card-rarity-${card.game.rarity} ${
          isTilting ? "is-tilting" : ""
        } ${editing ? "is-layout-editing" : ""
        }`}
        style={{
          transform: `perspective(1050px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${isTilting ? 0.96 : 1})`,
        }}
        onPointerDown={(event) => {
          if (editing) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          setIsTilting(true);
          updateTilt(event.clientX, event.clientY);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            updateTilt(event.clientX, event.clientY);
          }
        }}
        onPointerUp={resetTilt}
        onPointerCancel={resetTilt}
      >
        <div
          className="card-holo-glare"
          style={{
            opacity: isTilting ? 0.54 : 0.14,
            background:
              card.game.rarity === "UR"
                ? `radial-gradient(circle at ${tilt.lightX}% ${tilt.lightY}%, rgba(255,255,255,.68) 0 3%, rgba(118,244,255,.22) 13%, rgba(255,112,217,.15) 28%, transparent 52%), conic-gradient(from ${125 + tilt.y * 5}deg at ${tilt.lightX}% ${tilt.lightY}%, transparent 0 17%, rgba(90,235,255,.22) 23%, rgba(255,114,221,.2) 31%, rgba(255,236,92,.18) 39%, transparent 47% 100%)`
                : `radial-gradient(circle at ${tilt.lightX}% ${tilt.lightY}%, rgba(255,255,255,.68) 0 4%, rgba(255,245,190,.34) 15%, rgba(255,185,70,.11) 34%, transparent 55%), linear-gradient(${112 + tilt.y * 3}deg, transparent 34%, rgba(255,255,255,.05) 43%, rgba(255,250,207,.48) 49%, rgba(255,206,110,.14) 54%, transparent 64%)`,
          }}
        />
        <div className="card-rarity-effects" aria-hidden="true">
          <i className="card-sheen" />
          <i className="card-prism" />
          <i className="card-sparkles" />
        </div>
        <div className="collectible-card-inner">
          <MovableCardItem id="identity" editing={editing} selected={selectedLayoutItem === "identity"} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem} className="card-identity-position"><div className="card-identity">
            <span>{card.game.rarity}</span><strong>{card.name}</strong>
          </div></MovableCardItem>
          <MovableCardItem id="uploader" editing={editing} selected={selectedLayoutItem === "uploader"} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem} className="card-uploader-position"><span className="card-uploader">上传人：{card.author || "图鉴观察员"}</span></MovableCardItem>
          <CardPortrait card={card} />
          <div className="detail-card-copy">
            <div className="card-topline">
              <MovableCardItem id="code" editing={editing} selected={selectedLayoutItem === "code"} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem}><span>{card.code}</span></MovableCardItem>
              <MovableCardItem id="ai" editing={editing} selected={selectedLayoutItem === "ai"} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem}><span className="ai-badge">
                <Sparkles size={12} />
                AI 视觉观察
              </span></MovableCardItem>
            </div>
            <MovableCardItem id="location" editing={editing} selected={selectedLayoutItem === "location"} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem}><p className="detail-location">
              <MapPin size={15} />
              <AreaLabel area={card.area} />
            </p></MovableCardItem>
            <MovableCardItem id="traits" editing={editing} selected={selectedLayoutItem === "traits"} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem}><div className="trait-row">
              <span title="AI 建议，由上传人确认"><small>AI 外观</small>{cardCoat}</span>
              <span title="AI 建议，由上传人确认"><small>AI 特征</small>{cardFeature}</span>
            </div></MovableCardItem>
            <GameAttributes card={card} editing={editing} selected={selectedLayoutItem} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem} />
            <MovableCardItem id="observation" editing={editing} selected={selectedLayoutItem === "observation"} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem}><blockquote>{cardObservation}</blockquote></MovableCardItem>
            <MovableCardItem id="disclaimer" editing={editing} selected={selectedLayoutItem === "disclaimer"} layout={layout} onSelect={setSelectedLayoutItem} onMove={moveLayoutItem}><p className="disclaimer">
              外观观察与探索属性均为趣味化内容，不构成真实行为判断
            </p></MovableCardItem>
          </div>
        </div>
      </article>
      {editing && <div className="card-layout-toolbar">
        <div><strong>{card.game.rarity} 排版编辑</strong><span>{selectedLayoutItem ? `已选：${selectedLayoutItem}` : "点击文字后拖动；方向键微调"}</span></div>
        <button type="button" onClick={resetLayout}>重置</button>
        <button className="primary" type="button" onClick={saveLayout}>保存布局</button>
      </div>}
      {!editing && <p className="tilt-hint">按住卡片滑动，换个角度看看它 ✦</p>}
    </div>
  );
}

function CardPortrait({ card }: { card: CatCard }) {
  const portrait = card.cutoutImage ?? card.image;
  const [imageFailed, setImageFailed] = useState(false);
  const visiblePortrait = imageFailed ? undefined : portrait;
  return (
    <div className={`card-portrait ${visiblePortrait ? "has-portrait" : ""}`}>
      <div className="portrait-halo" aria-hidden="true" />
      {visiblePortrait ? (
        <img
          src={visiblePortrait}
          alt={`${card.name}的猫咪主体`}
          className={card.cutoutImage ? "cutout-cat" : "source-cat"}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="portrait-emoji" aria-hidden="true">
          {card.emoji}
        </span>
      )}
      {card.cutoutStatus === "pending" && (
        <span className="cutout-status">猫咪主体处理中</span>
      )}
      {card.cutoutStatus === "fallback" && (
        <span className="cutout-status fallback">原图预览，主体处理中</span>
      )}
      {imageFailed && <span className="cutout-status fallback">图片加载失败，请返回后重试</span>}
    </div>
  );
}

function HolographicCardSample({ card }: { card: CatCard }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [flipped, setFlipped] = useState(false);
  const flippedRef = useRef(false);
  const targetRotation = useRef({ x: 0.08, y: -0.16 });
  const dragStart = useRef<{ x: number; y: number; rx: number; ry: number } | null>(null);
  const portrait = card.cutoutImage ?? card.image;
  const template = {
    N: "/assets/card-template-n-v2.png",
    R: "/assets/card-template-r-v3.png",
    SR: "/assets/card-template-sr-v2.png",
    SSR: "/assets/card-template-ssr-v3.png",
    UR: "/assets/card-template-ur-v2.png",
  }[card.game.rarity];
  const coat = cardTextForDisplay(card.coat, "花色以照片为准");
  const feature = cardTextForDisplay(card.feature, "可见特征以照片为准");

  useEffect(() => { flippedRef.current = flipped; }, [flipped]);
  useEffect(() => {
    const host = stageRef.current;
    if (!host) return;
    let disposed = false;
    let frame = 0;
    let renderer: THREE.WebGLRenderer | undefined;
    let resizeObserver: ResizeObserver | undefined;
    const loaders = new THREE.TextureLoader();
    loaders.setCrossOrigin("anonymous");
    const makeTextTexture = (back = false) => {
      const canvas = document.createElement("canvas");
      canvas.width = 900; canvas.height = 1440;
      const context = canvas.getContext("2d");
      if (!context) return new THREE.CanvasTexture(canvas);
      const gradient = context.createLinearGradient(0, 0, 900, 1440);
      gradient.addColorStop(0, back ? "#1c2f74" : "rgba(16,48,92,0)");
      gradient.addColorStop(1, back ? "#38245e" : "rgba(28,14,71,.22)");
      if (back) context.fillStyle = gradient, context.fillRect(0, 0, 900, 1440);
      context.textAlign = "center";
      context.fillStyle = back ? "#f7d981" : "#fff9e3";
      context.font = "700 30px Arial, Microsoft YaHei, sans-serif";
      context.fillText(back ? "CITY ANIMAL ATLAS" : card.code, 450, back ? 230 : 92);
      context.font = "700 80px Arial, Microsoft YaHei, sans-serif";
      context.fillText(card.name, 450, back ? 420 : 1240);
      context.font = "500 34px Arial, Microsoft YaHei, sans-serif";
      const lines = back ? [coat, feature, "记录每一次相遇", "抬起手机，换个角度看看它"] : [`${card.area.city} · ${card.area.district}`, "CITY ANIMAL ATLAS"];
      lines.forEach((line, index) => context.fillText(line, 450, back ? 530 + index * 74 : 1300 + index * 46));
      context.strokeStyle = back ? "rgba(250,220,125,.8)" : "rgba(255,255,255,.7)";
      context.lineWidth = 3;
      context.strokeRect(48, 48, 804, 1344);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      return texture;
    };
    const makeFallbackTexture = () => {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 512;
      const context = canvas.getContext("2d");
      if (context) { context.font = "300px serif"; context.textAlign = "center"; context.textBaseline = "middle"; context.fillText(card.emoji, 256, 270); }
      return new THREE.CanvasTexture(canvas);
    };
    const loadTexture = (url: string | undefined, fallback: THREE.Texture) => new Promise<THREE.Texture>((resolve) => {
      if (!url) { resolve(fallback); return; }
      loaders.load(url, (texture) => { texture.colorSpace = THREE.SRGBColorSpace; resolve(texture); }, undefined, () => resolve(fallback));
    });

    void Promise.all([loadTexture(template, makeTextTexture()), loadTexture(portrait, makeFallbackTexture())]).then(([background, subject]) => {
      if (disposed) { background.dispose(); subject.dispose(); return; }
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(31, 1, 0.1, 100);
      camera.position.set(0, 0, 7.4);
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      host.replaceChildren(renderer.domElement);
      const cardGroup = new THREE.Group();
      scene.add(cardGroup);
      const cardWidth = 3.1, cardHeight = 4.96;
      const body = new THREE.Mesh(new THREE.BoxGeometry(cardWidth, cardHeight, 0.12), new THREE.MeshStandardMaterial({ color: "#132a6b", metalness: .72, roughness: .27 }));
      cardGroup.add(body);
      const plane = new THREE.PlaneGeometry(cardWidth, cardHeight);
      const front = new THREE.Mesh(plane, new THREE.MeshBasicMaterial({ map: background, transparent: true }));
      front.position.z = .071;
      cardGroup.add(front);
      const subjectMesh = new THREE.Mesh(new THREE.PlaneGeometry(2.58, 3.2), new THREE.MeshBasicMaterial({ map: subject, transparent: true, depthWrite: false }));
      subjectMesh.position.set(0, .28, .23);
      cardGroup.add(subjectMesh);
      const textTexture = makeTextTexture();
      const textMesh = new THREE.Mesh(plane, new THREE.MeshBasicMaterial({ map: textTexture, transparent: true, depthWrite: false }));
      textMesh.position.z = .3;
      cardGroup.add(textMesh);
      const foilMaterial = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        uniforms: { uTime: { value: 0 }, uTilt: { value: new THREE.Vector2() } },
        vertexShader: "varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }",
        fragmentShader: "uniform float uTime; uniform vec2 uTilt; varying vec2 vUv; vec3 spectrum(float t){ return .55+.45*cos(6.28318*(vec3(.0,.33,.67)+t)); } void main(){ float band=sin((vUv.x*8.0+vUv.y*3.5+uTilt.x*4.0-uTilt.y*2.0)+uTime*.45); float flare=smoothstep(.55,.98,band)*(.2+.32*vUv.y); float edge=pow(1.0-abs(vUv.x-.5)*2.0,2.0); gl_FragColor=vec4(spectrum(vUv.x*.7+vUv.y*.25+uTime*.03)*flare,flare*edge); }",
      });
      const foil = new THREE.Mesh(plane, foilMaterial);
      foil.position.z = .35;
      cardGroup.add(foil);
      const back = new THREE.Mesh(plane, new THREE.MeshBasicMaterial({ map: makeTextTexture(true) }));
      back.position.z = -.071;
      back.rotation.y = Math.PI;
      cardGroup.add(back);
      const clock = new THREE.Clock();
      const resize = () => {
        const { width, height } = host.getBoundingClientRect();
        if (!renderer || !width || !height) return;
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      };
      resize();
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(host);
      const render = () => {
        if (disposed || !renderer) return;
        const destinationY = targetRotation.current.y + (flippedRef.current ? Math.PI : 0);
        cardGroup.rotation.x += (targetRotation.current.x - cardGroup.rotation.x) * .09;
        cardGroup.rotation.y += (destinationY - cardGroup.rotation.y) * .09;
        foilMaterial.uniforms.uTime.value = clock.getElapsedTime();
        foilMaterial.uniforms.uTilt.value.set(cardGroup.rotation.y, cardGroup.rotation.x);
        renderer.render(scene, camera);
        frame = requestAnimationFrame(render);
      };
      render();
    }).catch(() => undefined);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      renderer?.dispose();
      host.replaceChildren();
    };
  }, [card.code, card.emoji, card.name, card.area.city, card.area.district, coat, feature, portrait, template]);

  return (
    <section className="three-holo-sample" aria-label="真实 3D 全息卡预览">
      <div className="three-holo-heading">
        <div><span>TRUE 3D HOLOGRAPHIC PROTOTYPE</span><strong>全息卡预览</strong></div>
        <button type="button" onClick={() => setFlipped((value) => !value)}>{flipped ? "查看正面" : "翻到背面"}</button>
      </div>
      <div
        ref={stageRef}
        className="three-holo-stage"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          dragStart.current = { x: event.clientX, y: event.clientY, rx: targetRotation.current.x, ry: targetRotation.current.y };
        }}
        onPointerMove={(event) => {
          if (!dragStart.current) return;
          targetRotation.current = {
            x: Math.max(-.28, Math.min(.28, dragStart.current.rx - (event.clientY - dragStart.current.y) * .004)),
            y: Math.max(-.38, Math.min(.38, dragStart.current.ry + (event.clientX - dragStart.current.x) * .004)),
          };
        }}
        onPointerUp={() => { dragStart.current = null; }}
        onPointerCancel={() => { dragStart.current = null; }}
      />
      <p className="three-holo-hint">拖动卡面看真实分层景深与视角镭射 · 点击“翻到背面”查看背卡</p>
    </section>
  );
}

function DetailScreen({
  card,
  comments,
  onBack,
  requireLogin,
  onComment,
  hasBoosted,
  onBoost,
  isSaved,
  onToggleSave,
  showToast,
  sessionToken,
  sessionUserId,
  canDelete,
  onDelete,
  onDeleteComment,
  onCardUpdated,
}: {
  card: CatCard;
  comments: Comment[];
  onBack: () => void;
  requireLogin: (action: () => void) => void;
  onComment: (comment: Comment) => void;
  hasBoosted: boolean;
  onBoost: (cardId: string) => void;
  isSaved: boolean;
  onToggleSave: () => void;
  showToast: (text: string) => void;
  sessionToken?: string;
  sessionUserId?: string;
  canDelete: boolean;
  onDelete: () => void;
  onDeleteComment: (commentId: string) => Promise<boolean>;
  onCardUpdated: (card: CatCard) => void;
}) {
  const [liked, setLiked] = useState(false);
  const [draft, setDraft] = useState("");
  const [image, setImage] = useState<string>();
  const [imageFile, setImageFile] = useState<File>();
  const [remoteComments, setRemoteComments] = useState<Comment[]>([]);
  const [reportOpen, setReportOpen] = useState(false);
  const [layoutEditing, setLayoutEditing] = useState(false);
  const [holographicPreview, setHolographicPreview] = useState(false);
  const [reviewEditing, setReviewEditing] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewDraft, setReviewDraft] = useState({
    name: card.name,
    coat: card.coat,
    feature: card.feature,
    observation: card.observation,
    city: card.area.city,
    district: card.area.district,
    street: card.area.street,
  });
  const shareCardRef = useRef<HTMLDivElement>(null);
  const isPublished = !card.moderationStatus || card.moderationStatus === "published";
  const published = useMemo(
    () => [...remoteComments, ...comments.filter((comment) => comment.status === "published")],
    [comments, remoteComments],
  );
  useEffect(() => {
    if (!COMMUNITY_ENABLED || !isPublished) {
      setRemoteComments([]);
      return;
    }
    void fetchComments("card", card.id).then(({ comments: items }) => setRemoteComments(items.map((item) => ({ id: item.id, catId: item.targetId, author: item.author, avatar: item.author.slice(0, 1), text: item.text, image: item.imageUrl, createdAt: "刚刚", status: "published", isMine: item.authorId === sessionUserId })))).catch(() => setRemoteComments([]));
  }, [card.id, sessionUserId, isPublished]);
  useEffect(() => {
    setReviewEditing(false);
    setReviewDraft({
      name: card.name,
      coat: card.coat,
      feature: card.feature,
      observation: card.observation,
      city: card.area.city,
      district: card.area.district,
      street: card.area.street,
    });
  }, [card.id, card.moderationStatus]);
  const currentRarityIndex = rarityOrder.indexOf(card.game.rarity);
  const nextRarity =
    currentRarityIndex < rarityOrder.indexOf("SSR")
      ? rarityOrder[currentRarityIndex + 1]
      : undefined;
  const boostProgress = Math.min(100, card.boostCount);
  const boost = () =>
    requireLogin(() => {
      if (!nextRarity) {
        showToast(
          card.game.rarity === "SSR"
            ? "SSR 不开放助力，UR 将由官方票选活动产生"
            : "这张卡已经是 UR",
        );
        return;
      }
      if (hasBoosted) {
        showToast("你已经为这张猫咪卡助力过了");
        return;
      }
      onBoost(card.id);
    });
  const submit = () =>
    requireLogin(() => {
      if (!draft.trim() && !image) {
        showToast("写点观察，或附上一张照片吧");
        return;
      }
      if (!sessionToken) { showToast("登录状态已失效，请重新登录"); return; }
      void (async () => {
        try {
          const imageUrl = imageFile ? await uploadImage(imageFile, sessionToken) : undefined;
          const saved = await createServerComment(sessionToken, { targetType: "card", targetId: card.id, text: draft.trim() || "图片评论", imageUrl });
          onComment({ id: saved.comment.id, catId: card.id, author: "我", avatar: "我", text: draft.trim() || "图片评论", image: imageUrl, createdAt: "刚刚", status: "pending", isMine: true });
          setDraft(""); setImage(undefined); setImageFile(undefined);
        } catch { showToast("评论提交失败，请稍后重试"); }
      })();
    });
  const submitForReview = () => {
    if (!sessionToken) {
      showToast("登录状态已失效，请重新登录");
      return;
    }
    if (!reviewDraft.name.trim() || !reviewDraft.coat.trim() || !reviewDraft.feature.trim() || !reviewDraft.city.trim() || !reviewDraft.district.trim()) {
      showToast("请补全名称、外观标签和城市区域");
      return;
    }
    setReviewSubmitting(true);
    void resubmitCard({
      ...card,
      name: reviewDraft.name.trim(),
      coat: reviewDraft.coat.trim(),
      feature: reviewDraft.feature.trim(),
      observation: reviewDraft.observation.trim(),
      area: {
        ...card.area,
        city: reviewDraft.city.trim(),
        district: reviewDraft.district.trim(),
        street: reviewDraft.street.trim(),
      },
    }, sessionToken).then(({ card: updated }) => {
      onCardUpdated(updated);
      setReviewEditing(false);
      showToast("已重新提交审核，可在我的图鉴查看进度");
    }).catch((error: unknown) => {
      showToast(error instanceof Error ? error.message : "重新提交失败，请稍后重试");
    }).finally(() => setReviewSubmitting(false));
  };
  const share = () => void (async () => {
    if (!shareCardRef.current) return;
    try {
      shareCardRef.current.classList.add("is-exporting");
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      const capture = await snapdom(shareCardRef.current, { scale: 2, embedFonts: true });
      const blob = await capture.toBlob({ type: "png" });
      const file = new File([blob], `${card.code}-城市动物图鉴.png`, { type: "image/png" });
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({ title: `${card.name} · 城市动物图鉴`, text: "记录每一次相遇", files: [file] });
      } else {
        const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = file.name; link.click(); URL.revokeObjectURL(link.href);
      }
    } catch { showToast("卡牌导出失败，请稍后重试"); }
    finally { shareCardRef.current?.classList.remove("is-exporting"); }
  })();
  return (
    <div className="subscreen detail-screen">
      <SubHeader
        title="猫咪卡"
        onBack={onBack}
        right={
          <div className="subheader-actions">
            {canDelete && (
              <button className="icon-btn danger-icon-btn" type="button" onClick={onDelete} aria-label="删除动物卡">
                <Trash2 size={19} />
              </button>
            )}
            {COMMUNITY_ENABLED && isPublished && (
              <button
                className="icon-btn"
                onClick={() => setReportOpen(true)}
                aria-label="举报"
              >
                <MoreHorizontal size={21} />
              </button>
            )}
          </div>
        }
      />
      <main className="detail-content">
        <button className="detail-back-btn" type="button" onClick={onBack}>
          <ArrowLeft size={18} /> 返回图鉴
        </button>
        <button
          className={`holographic-preview-toggle ${holographicPreview ? "is-active" : ""}`}
          type="button"
          onClick={() => setHolographicPreview((value) => !value)}
        >
          <Sparkles size={15} /> {holographicPreview ? "返回标准卡面" : "试试看全息卡"}
        </button>
        {!isPublished && (
          <section className={`card-review-panel ${card.moderationStatus}`}>
            <div className="card-review-heading">
              {card.moderationStatus === "hidden" ? <AlertTriangle size={20} /> : <ShieldCheck size={20} />}
              <div>
                <strong>{card.moderationStatus === "hidden" ? "审核未通过" : "正在审核"}</strong>
                <p>
                  {card.moderationStatus === "hidden"
                    ? card.moderationReason || "请调整卡片信息后重新提交审核。"
                    : "这张卡目前仅你可见，审核通过后会出现在发现页并计入图鉴挑战。"}
                </p>
              </div>
            </div>
            {card.moderationStatus === "hidden" && !reviewEditing && (
              <button className="review-edit-btn" type="button" onClick={() => setReviewEditing(true)}>
                修改后重新提交
              </button>
            )}
            {card.moderationStatus === "hidden" && reviewEditing && (
              <div className="review-edit-form">
                <label>动物名称<input value={reviewDraft.name} maxLength={20} onChange={(event) => setReviewDraft((draft) => ({ ...draft, name: event.target.value }))} /></label>
                <div className="review-edit-grid">
                  <label>毛色标签<input value={reviewDraft.coat} maxLength={30} onChange={(event) => setReviewDraft((draft) => ({ ...draft, coat: event.target.value }))} /></label>
                  <label>外观特征<input value={reviewDraft.feature} maxLength={60} onChange={(event) => setReviewDraft((draft) => ({ ...draft, feature: event.target.value }))} /></label>
                </div>
                <label>AI 观察说明<textarea value={reviewDraft.observation} maxLength={160} onChange={(event) => setReviewDraft((draft) => ({ ...draft, observation: event.target.value }))} /></label>
                <div className="review-edit-grid location-grid">
                  <label>城市<input value={reviewDraft.city} maxLength={12} onChange={(event) => setReviewDraft((draft) => ({ ...draft, city: event.target.value }))} /></label>
                  <label>区/县<input value={reviewDraft.district} maxLength={12} onChange={(event) => setReviewDraft((draft) => ({ ...draft, district: event.target.value }))} /></label>
                  <label>街道/乡镇<input value={reviewDraft.street} maxLength={16} onChange={(event) => setReviewDraft((draft) => ({ ...draft, street: event.target.value }))} /></label>
                </div>
                <div className="review-edit-actions">
                  <button type="button" onClick={() => setReviewEditing(false)}>取消</button>
                  <button className="primary" type="button" disabled={reviewSubmitting} onClick={submitForReview}>
                    {reviewSubmitting ? "提交中…" : "重新提交审核"}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
        <div className="share-card-export" ref={shareCardRef}>
          {holographicPreview ? <HolographicCardSample card={card} /> : <CollectibleCard card={card} editing={layoutEditing} onEditingChange={setLayoutEditing} />}
          <p>城市动物图鉴 · 记录每一次相遇</p>
        </div>
        {isPublished && <div className="action-row">
          {COMMUNITY_ENABLED && <button
            onClick={() => {
              setLiked(!liked);
              showToast(liked ? "已取消喜欢" : "已喜欢这张卡");
            }}
            className={liked ? "liked" : ""}
          >
            <Heart size={19} fill={liked ? "currentColor" : "none"} />
            {card.likes + (liked ? 1 : 0)}
          </button>}
          <button onClick={share}>
            <Send size={18} />
            分享
          </button>
          <button
            className={isSaved ? "liked" : ""}
            onClick={onToggleSave}
          >
            <Bookmark size={18} fill={isSaved ? "currentColor" : "none"} />
            收藏
          </button>
        </div>}
        {COMMUNITY_ENABLED && isPublished && <button
          className={`boost-panel ${hasBoosted ? "is-boosted" : ""}`}
          onClick={boost}
          disabled={Boolean(!nextRarity || hasBoosted)}
        >
          <span className="boost-icon">✦</span>
          <span className="boost-copy">
            <strong>
              {nextRarity
                ? hasBoosted
                  ? "你已助力这张猫咪卡"
                  : `助力升至 ${nextRarity}`
                : card.game.rarity === "SSR"
                  ? "SSR 不开放助力"
                  : "已是最高稀有度 UR"}
            </strong>
            <small>
              {nextRarity
                ? `集满 100 人助力，即可从 ${card.game.rarity} 升至 ${nextRarity}`
                : card.game.rarity === "SSR"
                  ? "UR 将在后续官方全平台票选活动中诞生"
                  : "UR 由官方全平台票选活动产生"}
            </small>
          </span>
          <span className="boost-count">
            {nextRarity ? (
              <>
                <b>{boostProgress}</b>
                <small>/ 100 人</small>
              </>
            ) : (
              <>
                <b>{card.game.rarity}</b>
                <small>活动机制</small>
              </>
            )}
          </span>
          <span className="boost-progress" aria-hidden="true">
            <i style={{ width: `${boostProgress}%` }} />
          </span>
          <ChevronRight size={17} className="boost-arrow" />
        </button>}
        {COMMUNITY_ENABLED && isPublished && <section className="comment-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">COMMUNITY NOTES</p>
              <h2>一起记住它</h2>
            </div>
            <span className="comment-count">{published.length} 条观察</span>
          </div>
          <div className="comment-list">
            {published.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onDelete={comment.isMine ? async () => {
                  if (await onDeleteComment(comment.id)) {
                    setRemoteComments((items) => items.filter((item) => item.id !== comment.id));
                  }
                } : undefined}
              />
            ))}
            {comments.some((comment) => comment.status === "pending") && (
              <div className="pending-note">
                <ShieldCheck size={16} />
                你有 1 条内容正在审核，审核通过后会公开展示。
              </div>
            )}
          </div>
        </section>}
      </main>
      {COMMUNITY_ENABLED && isPublished && <div className="comment-composer">
        <label className="attach-button">
          <ImagePlus size={19} />
          <input
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              if (!file.type.startsWith("image/") || file.size > 10 * 1024 * 1024) { showToast("请上传不超过 10MB 的图片"); return; }
              setImageFile(file);
              setImage(URL.createObjectURL(file));
            }}
          />
        </label>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, 200))}
          placeholder={image ? "为这张照片写一句观察…" : "写下你见到它的瞬间…"}
        />
        <button className="send-button" onClick={submit} aria-label="发布评论">
          <Send size={19} />
        </button>
        {image && (
          <div className="attached-preview">
            <img src={image} alt="待发布的评论图片" />
            <button onClick={() => setImage(undefined)}>
              <X size={13} />
            </button>
          </div>
        )}
      </div>}
      {COMMUNITY_ENABLED && reportOpen && (
        <ReportSheet
          onClose={() => setReportOpen(false)}
          onDone={() => {
            setReportOpen(false);
            showToast("举报已提交，我们会在 24 小时内处理");
          }}
        />
      )}
    </div>
  );
}

function GameAttributes({ card, editing, selected, layout, onSelect, onMove }: {
  card: CatCard;
  editing: boolean;
  selected?: CardLayoutKey;
  layout: CardLayout;
  onSelect: (id: CardLayoutKey) => void;
  onMove: (id: CardLayoutKey, x: number, y: number) => void;
}) {
  const stats = [
    ["胆量", card.game.courage],
    ["亲人度", card.game.affinity],
    ["好奇心", card.game.curiosity],
    ["潜行力", card.game.stealth],
    ["饭量", card.game.appetite],
    ["速度", card.game.speed],
    ["敏捷", card.game.agility],
    ["智力", card.game.intelligence],
  ] as const;
  return (
    <section className="game-panel">
      <MovableCardItem id="heading" editing={editing} selected={selected === "heading"} layout={layout} onSelect={onSelect} onMove={onMove}><div className="game-heading">
        <div>
          <p>EXPLORER PROFILE</p>
          <h2>探索属性</h2>
        </div>
      </div></MovableCardItem>
      <div className="game-stats">
        {stats.map(([label, value], index) => (
          <div className="game-stat" key={label}>
            <MovableCardItem
              id={`stat-label-${index}`}
              fallbackId={`stat-${index}`}
              editing={editing}
              selected={selected === `stat-label-${index}`}
              layout={layout}
              onSelect={onSelect}
              onMove={onMove}
              className="game-stat-label-item"
            >
              <span className="game-stat-label">{label}</span>
            </MovableCardItem>
            <MovableCardItem
              id={`stat-stars-${index}`}
              fallbackId={`stat-${index}`}
              editing={editing}
              selected={selected === `stat-stars-${index}`}
              layout={layout}
              onSelect={onSelect}
              onMove={onMove}
              className="game-stat-stars-item"
            >
              <strong className="game-stat-stars"><StarRating value={value} /></strong>
            </MovableCardItem>
          </div>
        ))}
      </div>
      <MovableCardItem id="footnote" editing={editing} selected={selected === "footnote"} layout={layout} onSelect={onSelect} onMove={onMove}><p className="game-footnote">上传者趣味设定 · 最低半星，满分五星</p></MovableCardItem>
    </section>
  );
}

function SubHeader({
  title,
  onBack,
  right,
}: {
  title: string;
  onBack: () => void;
  right?: React.ReactNode;
}) {
  return (
    <header className="sub-header">
      <button className="icon-btn" onClick={onBack} aria-label="返回">
        <ArrowLeft size={21} />
      </button>
      <strong>{title}</strong>
      {right ?? <span className="header-spacer" />}
    </header>
  );
}

function CommentItem({ comment, onDelete }: { comment: Comment; onDelete?: () => void }) {
  return (
    <article className="comment">
      <div className="comment-avatar">{comment.avatar}</div>
      <div>
        <div className="comment-meta">
          <strong>{comment.author}</strong>
          <span>{comment.createdAt}</span>
          {onDelete && (
            <button className="comment-delete" type="button" onClick={onDelete} aria-label="删除评论">
              <Trash2 size={13} />
            </button>
          )}
        </div>
        {comment.text && <p>{comment.text}</p>}
        {comment.image && (
          <img
            className="comment-image"
            src={comment.image}
            alt="用户分享的观察照片"
          />
        )}
      </div>
    </article>
  );
}

function ReportSheet({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  return (
    <div className="modal-backdrop">
      <section className="report-sheet">
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>
        <AlertTriangle size={28} />
        <h2>举报内容</h2>
        <p>若内容涉及虐待动物、隐私泄露、交易、骚扰或违法信息，请告诉我们。</p>
        {[
          "动物伤害或不当引导",
          "泄露位置、住址或个人隐私",
          "交易、联系方式或商业推广",
          "其他违法和不良信息",
        ].map((reason) => (
          <button key={reason} className="report-reason" onClick={onDone}>
            {reason}
            <ChevronRight size={17} />
          </button>
        ))}
      </section>
    </div>
  );
}
